<template>
  <!--
    StoneTechnicalDrawing2D
    =========================================================================
    Componente PRINCIPAL do desenho técnico 2D da ficha técnica.

    Responsabilidades (orquestração):
      - normaliza `items` (ficha_tecnica) em peças desenháveis;
      - mantém as camadas livres (elementos, anotações, imagens);
      - controla seleção, ferramenta ativa, zoom/pan e toggles;
      - traduz gestos do canvas em PATCHES e emite eventos para o pai persistir.

    NÃO faz: cálculo de preço, alteração da regra financeira, dependências
    pesadas (Three.js/WebGL). Tudo é SVG, pronto para export futuro.
  -->
  <div
    class="std-root"
    :class="{ 'std-root--dark': isDarkTheme, 'std-root--compact': compactBoard }"
    :style="{ height: heightStyle }"
  >
    <button
      v-if="compactBoard"
      class="std-top-toggle"
      type="button"
      @click="topControlsOpen = !topControlsOpen"
    >
      <v-icon size="17">{{ topControlsOpen ? 'mdi-chevron-up' : 'mdi-menu' }}</v-icon>
    </button>

    <!-- TOOLBAR -->
    <DrawingToolbar
      v-show="false && (!compactBoard || topControlsOpen)"
      :active-tool="activeTool"
      :show-grid="gridOn"
      :show-measurements="measurementsOn"
      :technical="technicalOn"
      :readonly="readonly"
      :can-undo="undoStack.length > 0"
      :can-redo="redoStack.length > 0"
      :iso-view="isoOn"
      :cutout-options="toolbarCutoutOptions"
      :side-open="sideOpen"
      @toggle-side="sideOpen = !sideOpen"
      @toggle-iso="isoOn = !isoOn"
      @set-tool="setTool"
      @toggle-grid="gridOn = !gridOn"
      @toggle-measurements="measurementsOn = !measurementsOn"
      @toggle-style="technicalOn = !technicalOn"
      @zoom-in="zoomBy(1.2)"
      @zoom-out="zoomBy(1 / 1.2)"
      @center="centerView"
      @reset-view="resetView"
      @undo="undo"
      @redo="redo"
    />

    <!-- PALETA de acabamentos cadastrados (Insumos type "Acabamento"):
         arraste um chip para cima da borda da peça para aplicar -->
    <div v-if="false && !readonly && edgeFinishCatalog.length && (!compactBoard || topControlsOpen)" class="std-finish-palette">
      <span class="std-finish-palette-label">
        <v-icon size="13">mdi-drag-variant</v-icon>
        Acabamentos — arraste para a borda:
      </span>
      <div
        v-for="(finish, fi) in edgeFinishCatalog"
        :key="`palette-${finish._id || fi}`"
        class="std-finish-chip"
        draggable="true"
        :title="`Arraste “${finish.nome}” para uma borda da peça`"
        @dragstart="onFinishDragStart($event, fi)"
      >
        <img
          v-if="finishImage(finish)"
          :src="finishImage(finish)"
          class="std-finish-chip-img"
          alt=""
        />
        <v-icon v-else size="14" class="std-finish-chip-img">mdi-texture-box</v-icon>
        <span>{{ finish.nome }}</span>
      </div>
    </div>

    <div class="std-body">
      <!-- ÁREA DO DESENHO -->
      <div ref="stage" class="std-stage">
        <div v-show="!compactBoard || topControlsOpen" class="std-board-head">
          <div class="std-board-title">
            <v-icon size="18">mdi-ruler-square-compass</v-icon>
            <div>
              <strong>{{ boardTitle }}</strong>
              <span v-if="boardSubtitle">{{ boardSubtitle }}</span>
            </div>
          </div>
          <DrawingToolbar
            class="std-board-toolbar"
            :active-tool="activeTool"
            :show-grid="gridOn"
            :show-measurements="measurementsOn"
            :technical="technicalOn"
            :readonly="readonly"
            :can-undo="undoStack.length > 0"
            :can-redo="redoStack.length > 0"
            :iso-view="isoOn"
            :cutout-options="toolbarCutoutOptions"
            :side-open="sideOpen"
            @toggle-side="sideOpen = !sideOpen"
            @toggle-iso="isoOn = !isoOn"
            @set-tool="setTool"
            @toggle-grid="gridOn = !gridOn"
            @toggle-measurements="measurementsOn = !measurementsOn"
            @toggle-style="technicalOn = !technicalOn"
            @zoom-in="zoomBy(1.2)"
            @zoom-out="zoomBy(1 / 1.2)"
            @center="centerView"
            @reset-view="resetView"
            @undo="undo"
            @redo="redo"
          />
          <div class="std-board-actions">
            <slot name="board-actions"></slot>
          </div>
        </div>
        <div class="std-piece-list-float" :class="{ collapsed: !pieceListOpen || compactBoard }">
          <button class="std-piece-list-toggle" type="button" @click="pieceListOpen = !pieceListOpen">
            <v-icon size="16">{{ pieceListOpen ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
          </button>
          <div v-if="pieceListOpen && !compactBoard" class="std-piece-list-content">
            <div class="std-piece-tabs">
              <button class="std-piece-tab" :class="{ active: listTab === 'pieces' }" type="button" @click="listTab = 'pieces'">
                Peças <span>{{ stonePiecesList.length }}</span>
              </button>
              <button class="std-piece-tab" :class="{ active: listTab === 'elements' }" type="button" @click="listTab = 'elements'">
                Elementos <span>{{ elements.length }}</span>
              </button>
              <button class="std-piece-tab" :class="{ active: listTab === 'images' }" type="button" @click="listTab = 'images'">
                Imagens <span>{{ images.length }}</span>
              </button>
            </div>
            <div class="std-piece-list-title">Peças da venda</div>
            <button
              v-for="piece in stonePiecesList"
              v-show="listTab === 'pieces'"
              :key="`piece-list-${piece.id}`"
              class="std-piece-list-item"
              :class="{ active: selection.kind === 'piece' && selection.id === piece.id }"
              type="button"
              @click="onSelect({ kind: 'piece', id: piece.id })"
            >
              <span class="std-piece-list-code">{{ pieceListCode(piece) }}</span>
              <span class="std-piece-list-name">{{ piece.description || 'Peça' }}</span>
              <span class="std-piece-list-size">{{ formatMeasure(piece.width) }} x {{ formatMeasure(piece.height) }}m</span>
            </button>
            <template v-if="listTab === 'elements'">
              <div v-if="!elements.length" class="std-piece-list-empty">Nenhum elemento adicionado.</div>
              <div
                v-for="el in elements"
                :key="`element-list-${el.id}`"
                class="std-piece-list-item std-piece-list-item--action"
                :class="{ active: selection.kind === 'element' && selection.id === el.id }"
                @click="onSelect({ kind: 'element', id: el.id })"
              >
                <span class="std-piece-list-code"><v-icon size="14">mdi-shape-outline</v-icon></span>
                <span class="std-piece-list-name">{{ el.label }}</span>
                <button v-if="!readonly" class="std-piece-list-remove" type="button" @click.stop="onRemoveEntity({ kind: 'element', id: el.id })">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </template>
            <template v-if="listTab === 'images'">
              <div v-if="!images.length" class="std-piece-list-empty">Nenhuma imagem adicionada.</div>
              <div
                v-for="img in images"
                :key="`image-list-${img.id}`"
                class="std-piece-list-item std-piece-list-item--action"
                :class="{ active: selection.kind === 'image' && selection.id === img.id }"
                @click="onSelect({ kind: 'image', id: img.id })"
              >
                <span class="std-piece-list-code"><v-icon size="14">mdi-image-outline</v-icon></span>
                <span class="std-piece-list-name">{{ img.label || 'Imagem' }}</span>
                <button v-if="!readonly" class="std-piece-list-remove" type="button" @click.stop="onRemoveEntity({ kind: 'image', id: img.id })">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </template>
          </div>
        </div>
        <!-- vista em perspectiva isométrica (somente leitura) -->
        <div
          v-if="!readonly && edgeFinishCatalog.length"
          class="std-finish-list-float"
          :class="{ collapsed: !finishListOpen || compactBoard, 'side-open': sideOpen }"
        >
          <button class="std-finish-list-toggle" type="button" @click="finishListOpen = !finishListOpen">
            <v-icon size="16">{{ finishListOpen ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
          </button>
          <div v-if="finishListOpen && !compactBoard" class="std-finish-list-content">
            <div class="std-finish-list-title">
              <v-icon size="14">mdi-texture-box</v-icon>
              Acabamentos
              <span>{{ edgeFinishCatalog.length }}</span>
            </div>
            <div
              v-for="(finish, fi) in edgeFinishCatalog"
              :key="`finish-list-${finish._id || fi}`"
              class="std-finish-chip"
              draggable="true"
              :title="`Arraste '${finish.nome}' para uma borda da peça`"
              @dragstart="onFinishDragStart($event, fi)"
            >
              <img
                v-if="finishImage(finish)"
                :src="finishImage(finish)"
                class="std-finish-chip-img"
                alt=""
              />
              <v-icon v-else size="14" class="std-finish-chip-img">mdi-texture-box</v-icon>
              <span>{{ finish.nome }}</span>
            </div>
          </div>
        </div>
        <IsometricView
          v-if="isoOn"
          :pieces="project.pieces"
          :elements="elements"
          :width="canvasWidth"
          :height="canvasHeight"
          :plan-width="canvasWidth"
          :plan-height="canvasHeight"
          :dark-theme="isDarkTheme"
          class="std-iso-frame"
        />
        <DrawingCanvas
          v-show="!isoOn"
          ref="canvas"
          :pieces="project.pieces"
          :elements="elements"
          :annotations="annotations"
          :images="images"
          :width="canvasWidth"
          :height="canvasHeight"
          :scale="project.scale"
          :zoom="zoom"
          :pan-x="pan.x"
          :pan-y="pan.y"
          :show-grid="gridOn"
          :show-measurements="measurementsOn"
          :technical-style="technicalOn"
          :dark-theme="isDarkTheme"
          :selection="selection"
          :active-tool="activeTool"
          :readonly="readonly"
          :ghost-config="ghostConfig"
          @select="onSelect"
          @edge-drop="onEdgeDropApply"
          @accept-secondary="onAcceptSecondary"
          @move-piece="onMovePiece"
          @move-entity="onMoveEntity"
          @move-leader="onMoveLeader"
          @move-cutout="onMoveCutout"
          @resize-cutout="onResizeCutout"
          @cutout-menu="onCutoutMenu"
          @resize-swatch="onResizeSwatch"
          @move-swatch="onMoveSwatch"
          @erase-rect="onEraseRect"
          @resize-entity="onResizeEntity"
          @resize-piece="onResizePiece"
          @shape-resize="onShapeResize"
          @edit-dimension="onEditDimension"
          @edge-click="onEdgeClick"
          @edge-menu="onEdgeMenu"
          @piece-menu="onPieceMenu"
          @annotation-menu="onAnnotationMenu"
          @canvas-insert="onCanvasInsert"
          @wheel-zoom="onWheelZoom"
          @pan-by="onPanBy"
          @edit-annotation="openAnnotationEditor"
          @create-wall="onCreateWall"
          @wall-draft-ended="activeTool = 'select'"
          @move-wall-point="onMoveWallPoint"
          @move-wall-bow="onMoveWallBow"
        />

        <!-- menu contextual de acabamento de borda (clique direito na borda) -->
        <div
          v-if="edgeMenu.visible"
          class="std-edge-menu-overlay"
          @mousedown.self="closeEdgeMenu"
          @contextmenu.prevent.self="closeEdgeMenu"
        >
          <EdgeFinishMenu
            :left="edgeMenu.left"
            :top="edgeMenu.top"
            :edge="edgeMenu.edge"
            :current-name="edgeMenuCurrentName"
            :edge-active="edgeMenuEdgeActive"
            :current-text-rotation="edgeMenuCurrentTextRotation"
            :catalog="edgeFinishCatalog"
            @select-insumo="applyEdgeMenuInsumo"
            @set-text-rotation="setEdgeFinishTextRotation"
            @remove-edge="removeEdgeMenuEdge"
          />
        </div>

        <!-- menu do copilot: qual Peça Secundária adicionar na borda -->
        <div
          v-if="secondaryMenu.visible"
          class="std-edge-menu-overlay"
          @mousedown.self="closeSecondaryMenu"
          @contextmenu.prevent.self="closeSecondaryMenu"
        >
          <div
            class="std-secondary-menu"
            :style="{ left: `${secondaryMenu.left}px`, top: `${secondaryMenu.top}px` }"
          >
            <!-- menu ABERTO: peça secundária OU acabamento, sem hierarquia fixa -->
            <template v-if="secondaryCatalog.length">
              <div class="std-secondary-menu-title">Peça secundária</div>
              <button
                v-for="(insumo, si) in secondaryCatalog"
                :key="`sec-opt-${insumo._id || si}`"
                class="std-secondary-menu-item"
                @click="applySecondaryMenuInsumo(insumo)"
              >
                <img
                  v-if="finishImage(insumo)"
                  :src="finishImage(insumo)"
                  class="std-secondary-menu-thumb"
                  alt=""
                />
                <v-icon v-else size="15" class="std-secondary-menu-thumb">
                  mdi-view-agenda-outline
                </v-icon>
                <span>{{ insumo.nome }}</span>
              </button>
            </template>
            <template v-if="edgeFinishCatalog.length">
              <div class="std-secondary-menu-title">Acabamento na borda</div>
              <button
                v-for="(insumo, fi) in edgeFinishCatalog"
                :key="`sec-fin-${insumo._id || fi}`"
                class="std-secondary-menu-item"
                @click="applySecondaryMenuFinish(insumo)"
              >
                <img
                  v-if="finishImage(insumo)"
                  :src="finishImage(insumo)"
                  class="std-secondary-menu-thumb"
                  alt=""
                />
                <v-icon v-else size="15" class="std-secondary-menu-thumb">
                  mdi-texture-box
                </v-icon>
                <span>{{ insumo.nome }}</span>
              </button>
            </template>
          </div>
        </div>

        <!-- menu contextual da peça (clique direito no corpo da peça) -->
        <div
          v-if="cutoutMenu.visible"
          class="std-edge-menu-overlay"
          @mousedown.self="closeCutoutMenu"
          @contextmenu.prevent.self="closeCutoutMenu"
        >
          <div
            class="std-piece-menu std-cutout-menu"
            :style="{ left: `${cutoutMenu.left}px`, top: `${cutoutMenu.top}px` }"
          >
            <div class="std-piece-menu-title">Furo / recorte</div>
            <button class="std-piece-menu-item" @click="openCutoutProperties">
              <v-icon size="16">mdi-tune</v-icon>
              <span>Propriedades</span>
            </button>
            <button class="std-piece-menu-item std-piece-menu-item--danger" @click="removeCutoutFromMenu">
              <v-icon size="16">mdi-delete-outline</v-icon>
              <span>Excluir</span>
            </button>
          </div>
        </div>

        <div
          v-if="pieceMenu.visible"
          class="std-edge-menu-overlay"
          @mousedown.self="closePieceMenu"
          @contextmenu.prevent.self="closePieceMenu"
        >
          <div
            class="std-piece-menu"
            :style="{ left: `${pieceMenu.left}px`, top: `${pieceMenu.top}px` }"
          >
            <template
              v-if="surfaceFinishCatalog.length || (pieceMenuPiece && pieceMenuPiece.surfaceFinishItem)"
            >
              <div class="std-piece-menu-title">Acabamento na superfície</div>
              <button
                v-for="(insumo, fi) in surfaceFinishCatalog"
                :key="`surface-fin-${insumo._id || fi}`"
                class="std-piece-menu-item"
                @click="applySurfaceFinishFromMenu(insumo)"
              >
                <img
                  v-if="finishImage(insumo)"
                  :src="finishImage(insumo)"
                  class="std-secondary-menu-thumb"
                  alt=""
                />
                <v-icon v-else size="15">mdi-texture-box</v-icon>
                <span>{{ insumo.nome }}</span>
              </button>
              <button
                v-if="pieceMenuPiece && pieceMenuPiece.surfaceFinishItem"
                class="std-piece-menu-item std-piece-menu-item--danger"
                @click="removeSurfaceFinishFromMenu"
              >
                <v-icon size="15">mdi-close</v-icon>
                <span>Remover acabamento da superfície</span>
              </button>
            </template>
            <div class="std-piece-menu-title">Réguas</div>
            <div class="std-ruler-picker">
              <button
                class="std-ruler-dot std-ruler-dot--top"
                :class="{ active: visualRulerOption('top').active }"
                title="Comprimento em cima"
                @click="toggleVisualRuler('top')"
              >
                <span class="std-ruler-mini-dim std-ruler-mini-dim--h"></span>
                <v-icon
                  size="12"
                  class="std-ruler-eye"
                  :class="{ active: visualRulerOption('top').visible }"
                  @click.stop="toggleVisualRuler('top')"
                >{{ visualRulerOption('top').visible ? 'mdi-eye' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
              <button
                class="std-ruler-dot std-ruler-dot--left"
                :class="{ active: visualRulerOption('left').active }"
                title="Largura à esquerda"
                @click="toggleVisualRuler('left')"
              >
                <span class="std-ruler-mini-dim std-ruler-mini-dim--v"></span>
                <v-icon
                  size="12"
                  class="std-ruler-eye"
                  :class="{ active: visualRulerOption('left').visible }"
                  @click.stop="toggleVisualRuler('left')"
                >{{ visualRulerOption('left').visible ? 'mdi-eye' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
              <div class="std-ruler-preview">
                <span></span>
              </div>
              <button
                class="std-ruler-dot std-ruler-dot--right"
                :class="{ active: visualRulerOption('right').active }"
                title="Largura à direita"
                @click="toggleVisualRuler('right')"
              >
                <span class="std-ruler-mini-dim std-ruler-mini-dim--v"></span>
                <v-icon
                  size="12"
                  class="std-ruler-eye"
                  :class="{ active: visualRulerOption('right').visible }"
                  @click.stop="toggleVisualRuler('right')"
                >{{ visualRulerOption('right').visible ? 'mdi-eye' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
              <button
                class="std-ruler-dot std-ruler-dot--bottom"
                :class="{ active: visualRulerOption('bottom').active }"
                title="Comprimento embaixo"
                @click="toggleVisualRuler('bottom')"
              >
                <span class="std-ruler-mini-dim std-ruler-mini-dim--h"></span>
                <v-icon
                  size="12"
                  class="std-ruler-eye"
                  :class="{ active: visualRulerOption('bottom').visible }"
                  @click.stop="toggleVisualRuler('bottom')"
                >{{ visualRulerOption('bottom').visible ? 'mdi-eye' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
            </div>
            <div class="std-piece-menu-title">Rotacionar para:</div>
            <div class="std-piece-menu-split">
              <button class="std-piece-menu-item" title="Girar 90° à esquerda" @click="rotatePieceFromMenu(-90)">
                <v-icon size="16">mdi-rotate-left</v-icon>
                <span>Esquerda</span>
              </button>
              <button class="std-piece-menu-item" title="Girar 90° à direita" @click="rotatePieceFromMenu(90)">
                <v-icon size="16">mdi-rotate-right</v-icon>
                <span>Direita</span>
              </button>
            </div>
            <div class="std-piece-menu-title">Texto</div>
            <div class="std-piece-menu-split">
              <button class="std-piece-menu-item" title="Girar texto à esquerda" @click="rotatePieceTextDirection(-90)">
                <v-icon size="16">mdi-format-text-rotation-up</v-icon>
                <span>Esquerda</span>
              </button>
              <button class="std-piece-menu-item" title="Girar texto à direita" @click="rotatePieceTextDirection(90)">
                <v-icon size="16">mdi-format-text-rotation-down</v-icon>
                <span>Direita</span>
              </button>
            </div>
            <div class="std-piece-menu-title">Camada</div>
            <div class="std-piece-menu-stack">
              <button class="std-piece-menu-item" title="Trazer uma camada para frente" @click="movePieceLayerFromMenu(1)">
                <v-icon size="16">mdi-arrange-bring-forward</v-icon>
                <span>Trazer pra frente</span>
              </button>
              <button class="std-piece-menu-item" title="Enviar uma camada para trás" @click="movePieceLayerFromMenu(-1)">
                <v-icon size="16">mdi-arrange-send-backward</v-icon>
                <span>Mandar pra trás</span>
              </button>
            </div>
            <button class="std-piece-menu-item" @click="duplicatePieceFromMenu">
              <v-icon size="16">mdi-content-duplicate</v-icon>
              <span>Duplicar peça</span>
            </button>
            <button class="std-piece-menu-item std-piece-menu-item--danger" @click="removePieceFromMenu">
              <v-icon size="16">mdi-delete-outline</v-icon>
              <span>Excluir peça</span>
            </button>
          </div>
        </div>

        <!-- menu contextual da ANOTAÇÃO (clique direito no texto do board):
             mesma rotação em passos de 90° do texto da peça -->
        <div
          v-if="annotationMenu.visible"
          class="std-edge-menu-overlay"
          @mousedown.self="closeAnnotationMenu"
          @contextmenu.prevent.self="closeAnnotationMenu"
        >
          <div
            class="std-piece-menu"
            :style="{ left: `${annotationMenu.left}px`, top: `${annotationMenu.top}px` }"
          >
            <div class="std-piece-menu-title">Texto</div>
            <div class="std-piece-menu-split">
              <button class="std-piece-menu-item" title="Girar 90° à esquerda" @click="rotateAnnotationFromMenu(-90)">
                <v-icon size="16">mdi-format-text-rotation-up</v-icon>
                <span>Esquerda</span>
              </button>
              <button class="std-piece-menu-item" title="Girar 90° à direita" @click="rotateAnnotationFromMenu(90)">
                <v-icon size="16">mdi-format-text-rotation-down</v-icon>
                <span>Direita</span>
              </button>
            </div>
            <button class="std-piece-menu-item" @click="toggleAnnotationLeaderFromMenu">
              <v-icon size="16">mdi-vector-polyline</v-icon>
              <span>{{ annotationMenuHasLeader ? "Remover linha de chamada" : "Linha de chamada" }}</span>
            </button>
            <button class="std-piece-menu-item" @click="editAnnotationFromMenu">
              <v-icon size="16">mdi-pencil-outline</v-icon>
              <span>Editar texto</span>
            </button>
            <button class="std-piece-menu-item std-piece-menu-item--danger" @click="removeAnnotationFromMenu">
              <v-icon size="16">mdi-delete-outline</v-icon>
              <span>Excluir texto</span>
            </button>
          </div>
        </div>

        <!-- editor inline de cota: input flutuante sobre a cota clicada -->
        <div
          v-if="dimEditor.visible"
          class="std-dim-editor"
          :style="{ left: `${dimEditor.left}px`, top: `${dimEditor.top}px` }"
        >
          <input
            ref="dimInput"
            v-model="dimEditor.value"
            class="std-dim-input"
            @keyup.enter="confirmDimensionEdit"
            @keyup.esc="cancelDimensionEdit"
            @blur="cancelDimensionEdit"
          />
          <span class="std-dim-unit">m</span>
        </div>

        <!-- legenda + elementos abaixo do canvas (modo compacto) -->
        <div v-if="false" class="std-stage-foot">
          <DrawingElementList
            :elements="elements"
            :images="showImages ? images : []"
            :finishing-legend="finishingLegend"
            :selection="selection"
            :readonly="readonly"
            @select="onSelect"
            @remove="onRemoveEntity"
          />
        </div>
      </div>

      <!-- PAINEL LATERAL: propriedades + anotações.
           Recolhido por padrão (a manipulação é direta no canvas); abre pelo
           botão de ajustes na toolbar para os casos finos: medidas exatas de
           recorte, girar 90°, chamada da frente, opacidade/travar imagem,
           restaurar retângulo e remover peça. -->
      <div v-if="sideOpen" class="std-side" :class="{ 'std-side--dark': isDarkTheme }">
        <v-card outlined :dark="isDarkTheme" class="std-side-card">
          <div class="std-side-title">Propriedades</div>
          <DrawingInspector
            :selection="selection"
            :piece="selectedPiece"
            :element="selectedElement"
            :annotation="selectedAnnotation"
            :image="selectedImage"
            :cutout="selectedCutout"
            @update-piece="onInspectorPiecePatch"
            @update-element="patch => onUpdateEntity('element', selection.id, patch)"
            @update-annotation="patch => onUpdateEntity('annotation', selection.id, patch)"
            @update-image="patch => onUpdateEntity('image', selection.id, patch)"
            @update-cutout="onUpdateCutout"
            @remove="onRemoveEntity(selection)"
          />
        </v-card>

        <v-card v-if="showAnnotations" outlined :dark="isDarkTheme" class="std-side-card">
          <DrawingAnnotationPanel
            :annotations="annotations"
            :selection="selection"
            @add="addAnnotationAtCenter"
            @select="onSelect"
            @update="({ id, patch }) => onUpdateEntity('annotation', id, patch)"
            @remove="onRemoveEntity"
          />
        </v-card>
      </div>
    </div>

    <!-- input oculto para upload de imagem (base64). O pai pode interceptar
         add-image para fazer upload real e devolver uma URL. -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="std-hidden-file"
      @change="onFileSelected"
    />
  </div>
</template>

<script>
import DrawingToolbar from "./DrawingToolbar.vue";
import DrawingCanvas from "./DrawingCanvas.vue";
import DrawingInspector from "./DrawingInspector.vue";
import DrawingElementList from "./DrawingElementList.vue";
import DrawingAnnotationPanel from "./DrawingAnnotationPanel.vue";
import EdgeFinishMenu from "./EdgeFinishMenu.vue";
import IsometricView from "./IsometricView.vue";

import { buildDrawingProject, getFinishingLegend, getRenderablePieces } from "./engine/drawing/drawingFactory";
import { getContentBounds, getPieceBounds, formatMeasure } from "./engine/drawing/measurementUtils";
import {
  createCutout,
  clampCutoutToPiece,
  cutoutFromInsumo,
  getCutoutMeta,
  CUTOUT_TYPES
} from "./engine/drawing/cutoutUtils";
import {
  createDrawingElement,
  createAnnotation,
  createDrawingImage,
  getElementMeta,
  uid
} from "./engine/drawing/annotationUtils";
import { createWallElement, computeWallAngles } from "./engine/drawing/wallUtils";

export default {
  name: "StoneTechnicalDrawing2D",
  components: {
    DrawingToolbar,
    DrawingCanvas,
    DrawingInspector,
    DrawingElementList,
    DrawingAnnotationPanel,
    EdgeFinishMenu,
    IsometricView
  },
  props: {
    // FONTE DA VERDADE: product.ficha_tecnica
    items: { type: Array, required: true },
    selectedIndex: { type: Number, default: null },
    readonly: { type: Boolean, default: false },
    // dimensões LÓGICAS do desenho (viewBox). O SVG escala via CSS.
    width: { type: Number, default: 760 },
    height: { type: Number, default: 360 },
    boardTitle: { type: String, default: "Desenho técnico" },
    boardSubtitle: { type: String, default: "" },
    showGrid: { type: Boolean, default: true },
    showMeasurements: { type: Boolean, default: true },
    showAnnotations: { type: Boolean, default: true },
    showImages: { type: Boolean, default: true },
    // taxa financeira de acabamento (%). Só informativa aqui; não recalcula.
    taxFinishing: { type: [Number, String], default: 30 },
    // imagem da chapa (textura) — reservada p/ uso futuro como fundo.
    plateImage: { type: String, default: "" },
    // grossura da chapa no produto pai; garante o 3D correto em fichas antigas
    // que ainda não possuem a espessura copiada item a item.
    slabThicknessMm: { type: [Number, String], default: null },
    // inicia no estilo "prancha técnica" (line-art) em vez de colorido
    technicalStyle: { type: Boolean, default: false },
    // hidratação das camadas livres (caso o pai já persista o desenho)
    drawingElements: { type: Array, default: () => [] },
    drawingAnnotations: { type: Array, default: () => [] },
    drawingImages: { type: Array, default: () => [] },
    // (opcional) função (item) => item que recalcula preco/calculo/taxa/total
    // quando as MEDIDAS mudam pelo desenho. Injetada pelo pai (ex.: painel da
    // venda usa fichaPricing) — o engine continua sem regra financeira própria.
    itemRecalculator: { type: Function, default: null },
    // insumos type "Acabamento" cadastrados (imagem + preço): viram a paleta
    // arrastável e o menu da borda. Aplicar um deles na borda também ADICIONA
    // o item cobrado na ficha (via itemRecalculator).
    finishCatalog: { type: Array, default: () => [] },
    // insumos type "Peça Secundária" (saia, rodapé...): habilitam a
    // sugestão-fantasma nas bordas da peça principal selecionada.
    secondaryCatalog: { type: Array, default: () => [] },
    // insumos type "Furo" cadastrados (medidas em cm + símbolo padrão):
    // viram o dropdown "Inserir recorte". Vazio => símbolos padrão.
    cutoutCatalog: { type: Array, default: () => [] }
  },
  data() {
    return {
      activeTool: "select",
      gridOn: this.showGrid,
      measurementsOn: this.showMeasurements,
      technicalOn: this.technicalStyle,
      // vista em perspectiva isométrica (somente leitura)
      isoOn: false,
      // painel lateral de propriedades (recolhido por padrão)
      sideOpen: false,
      pieceListOpen: true,
      finishListOpen: true,
      listTab: "pieces",
      compactBoard: false,
      topControlsOpen: true,
      zoom: 1,
      pan: { x: 0, y: 0 },
      selection: { kind: null, id: null },
      // camadas livres (cópias locais; emitimos eventos para o pai persistir)
      elements: [],
      annotations: [],
      images: [],
      pendingImagePoint: null,
      // editor inline de cota (aberto por duplo clique na cota)
      dimEditor: { visible: false, pieceId: null, axis: null, value: "", left: 0, top: 0 },
      // menu contextual de acabamento (clique direito numa borda)
      edgeMenu: { visible: false, pieceId: null, edge: null, left: 0, top: 0 },
      cutoutMenu: { visible: false, id: null, left: 0, top: 0 },
      // menu do copilot: escolher qual Peça Secundária adicionar na borda
      secondaryMenu: { visible: false, pieceId: null, edge: null, left: 0, top: 0 },
      pieceMenu: { visible: false, pieceId: null, left: 0, top: 0 },
      // menu contextual da anotação de texto (clique direito no board)
      annotationMenu: { visible: false, id: null, left: 0, top: 0 },
      // undo/redo por snapshot (JSON de items + camadas livres)
      undoStack: [],
      redoStack: [],
      historyLastTag: null,
      historyLastTime: 0
    };
  },
  computed: {
    // projeto normalizado derivado da ficha (peças prontas para desenho)
    project() {
      return buildDrawingProject(this.itemsForDrawing(), {
        elements: this.elements,
        annotations: this.annotations,
        images: this.images,
        scale: 1
      });
    },
    finishingLegend() {
      return getFinishingLegend(this.project.pieces);
    },
    stonePiecesList() {
      return this.project.pieces.filter(piece => piece.isStone);
    },
    heightStyle() {
      return typeof this.height === "number" ? `${this.height}px` : this.height;
    },
    canvasWidth() {
      return 760;
    },
    canvasHeight() {
      return 480;
    },
    selectedPiece() {
      if (this.selection.kind !== "piece") return null;
      return this.project.pieces.find(p => p.id === this.selection.id) || null;
    },
    selectedElement() {
      if (this.selection.kind !== "element") return null;
      return this.elements.find(e => e.id === this.selection.id) || null;
    },
    selectedAnnotation() {
      if (this.selection.kind !== "annotation") return null;
      return this.annotations.find(a => a.id === this.selection.id) || null;
    },
    selectedImage() {
      if (this.selection.kind !== "image") return null;
      return this.images.find(i => i.id === this.selection.id) || null;
    },
    selectedCutout() {
      if (this.selection.kind !== "cutout") return null;
      for (const piece of this.project.pieces) {
        const cutout = (piece.cutouts || []).find(c => c.id === this.selection.id);
        if (cutout) return cutout;
      }
      return null;
    },
    edgeMenuPiece() {
      if (!this.edgeMenu.visible) return null;
      return this.project.pieces.find(p => p.id === this.edgeMenu.pieceId) || null;
    },
    edgeMenuCurrentName() {
      const piece = this.edgeMenuPiece;
      const info =
        piece && piece.edgeFinishItems && piece.edgeFinishItems[this.edgeMenu.edge];
      return (info && info.nome) || "";
    },
    edgeMenuCurrentTextRotation() {
      const piece = this.edgeMenuPiece;
      const info = piece && piece.edgeFinishItems && piece.edgeFinishItems[this.edgeMenu.edge];
      return Number(info && info.labelRotation) || 0;
    },
    edgeMenuEdgeActive() {
      const piece = this.edgeMenuPiece;
      return !!(
        piece &&
        Array.isArray(piece.edgeFinishes) &&
        piece.edgeFinishes.includes(this.edgeMenu.edge)
      );
    },
    edgeFinishCatalog() {
      return this.finishCatalog.filter(
        insumo => !insumo || !insumo.finish_target || insumo.finish_target === "borda"
      );
    },
    surfaceFinishCatalog() {
      return this.finishCatalog.filter(
        insumo => insumo && insumo.finish_target === "peca"
      );
    },
    // condição oficial de tema escuro do sistema
    isDarkTheme() {
      return localStorage.dark === "true";
    },
    // dropdown "Inserir recorte": insumos type "Furo" cadastrados; sem
    // cadastro, os símbolos padrão do sistema continuam disponíveis
    toolbarCutoutOptions() {
      if (this.cutoutCatalog.length) {
        return this.cutoutCatalog.map((insumo, index) => {
          const meta = getCutoutMeta((insumo.furo_config || {}).kind);
          return { key: `ins-${index}`, label: insumo.nome, icon: meta.icon };
        });
      }
      return CUTOUT_TYPES.map(meta => ({
        key: meta.kind,
        label: meta.label,
        icon: meta.icon
      }));
    },
    // COPILOT das bordas ABERTO: qualquer peça de pedra pode receber uma
    // Peça Secundária ou um Acabamento — o menu combinado oferece os dois
    pieceMenuPiece() {
      if (!this.pieceMenu.visible) return null;
      return this.project.pieces.find(p => p.id === this.pieceMenu.pieceId) || null;
    },
    annotationMenuHasLeader() {
      if (!this.annotationMenu.visible) return false;
      const ann = this.findEntity("annotation", this.annotationMenu.id);
      return !!(ann && ann.leader);
    },
    pieceMenuDimSides() {
      const piece = this.pieceMenuPiece;
      const sides = (piece && piece.dimensionSides) || {};
      return {
        lengthSide: sides.lengthSide || "bottom",
        widthSide: sides.widthSide || "right"
      };
    },
    pieceMenuTextDirection() {
      const piece = this.pieceMenuPiece;
      return Number(piece && piece.labelTextDirection) || 0;
    },
    textDirectionOptions() {
      return [
        { value: 0, icon: "mdi-format-text", title: "Texto horizontal" },
        { value: 90, icon: "mdi-format-rotate-90", title: "Texto 90 graus" },
        { value: 180, icon: "mdi-format-text-rotation-down", title: "Texto invertido" },
        { value: 270, icon: "mdi-format-text-rotation-up", title: "Texto 270 graus" }
      ];
    },
    ghostConfig() {
      const enabled =
        !this.readonly &&
        (this.secondaryCatalog.length || this.edgeFinishCatalog.length);
      return { label: enabled ? "Adicionar" : "" };
    }
  },
  watch: {
    drawingElements: { immediate: true, handler(v) { this.elements = (v || []).map(e => ({ ...e })); } },
    drawingAnnotations: { immediate: true, handler(v) { this.annotations = (v || []).map(a => ({ ...a })); } },
    drawingImages: { immediate: true, handler(v) { this.images = (v || []).map(i => ({ ...i })); } },
    selectedIndex: {
      immediate: true,
      handler(idx) {
        if (idx === null || idx === undefined) return;
        const piece = this.project.pieces.find(p => p.sourceIndex === idx);
        if (piece) this.selection = { kind: "piece", id: piece.id };
      }
    }
  },
  mounted() {
    // abre já enquadrado no conteúdo (melhor primeira impressão do editor)
    this.updateCompactBoard();
    this.$nextTick(() => this.centerView());
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("resize", this.updateCompactBoard);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("resize", this.updateCompactBoard);
  },
  methods: {
    formatMeasure,
    updateCompactBoard() {
      const el = this.$el;
      const w = el ? el.clientWidth : window.innerWidth;
      const h = el ? el.clientHeight : window.innerHeight;
      const compact = w < 820 || h < 560;
      if (compact !== this.compactBoard) {
        this.compactBoard = compact;
        this.topControlsOpen = !compact;
      }
    },
    itemsForDrawing() {
      const parentThickness = Number(`${this.slabThicknessMm || ""}`.replace(",", "."));
      const items = (this.items || []).map(item => {
        const normalizedType = `${(item && item.type) || ""}`
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
        if (
          !Number.isFinite(parentThickness) ||
          parentThickness <= 0 ||
          !normalizedType.includes("peca")
        ) {
          return item;
        }
        return {
          ...item,
          slabThicknessMm: parentThickness,
          espessura_mm: parentThickness,
          espessuraMm: parentThickness
        };
      });
      const chargedEdges = new Set(
        items
          .filter(item => item && item.edgeRef && item.edgeRef.parentId)
          .map(item => `${item.edgeRef.parentId}:${item.edgeRef.edge}`)
      );
      const chargedSurfaces = new Set(
        items
          .filter(item => item && item.surfaceRef && item.surfaceRef.parentId)
          .map(item => item.surfaceRef.parentId)
      );
      return items.map(item => {
        if (!item || !item.fichaId) return item;
        const edgeFinishItems = {};
        Object.keys(item.edgeFinishItems || {}).forEach(edge => {
          if (chargedEdges.has(`${item.fichaId}:${edge}`)) {
            edgeFinishItems[edge] = item.edgeFinishItems[edge];
          }
        });
        return {
          ...item,
          edgeFinishItems,
          surfaceFinishItem: chargedSurfaces.has(item.fichaId)
            ? item.surfaceFinishItem || null
            : null,
          edgeFinishes: (item.edgeFinishes || []).filter(edge =>
            chargedEdges.has(`${item.fichaId}:${edge}`)
          )
        };
      });
    },
    // ============ HISTÓRICO (undo/redo por snapshot) ============
    snapshot() {
      return JSON.stringify({
        items: this.items,
        elements: this.elements,
        annotations: this.annotations,
        images: this.images
      });
    },
    // grava o estado ANTES de uma mudança. `tag` agrupa gestos contínuos
    // (arrastes emitem dezenas de patches — só o primeiro vira histórico).
    pushHistory(tag) {
      const now = Date.now();
      if (this.historyLastTag === tag && now - this.historyLastTime < 600) {
        this.historyLastTime = now;
        return;
      }
      this.historyLastTag = tag;
      this.historyLastTime = now;
      this.undoStack.push(this.snapshot());
      if (this.undoStack.length > 30) this.undoStack.shift();
      this.redoStack = [];
    },
    undo() {
      if (!this.undoStack.length || this.readonly) return;
      this.redoStack.push(this.snapshot());
      this.restoreSnapshot(this.undoStack.pop());
    },
    redo() {
      if (!this.redoStack.length || this.readonly) return;
      this.undoStack.push(this.snapshot());
      this.restoreSnapshot(this.redoStack.pop());
    },
    restoreSnapshot(snap) {
      let data;
      try {
        data = JSON.parse(snap);
      } catch (e) {
        return;
      }
      this.historyLastTag = null;
      this.elements = data.elements || [];
      this.annotations = data.annotations || [];
      this.images = data.images || [];
      // itens voltam pelo fluxo normal de persistência do pai
      this.$emit("update:items", data.items || []);
      this.$emit("input", data.items || []);
      // camadas livres em bloco (o pai grava drawingExtras inteiro)
      this.$emit("replace-extras", {
        elements: this.elements,
        annotations: this.annotations,
        images: this.images
      });
    },

    // ============ ATALHOS DE TECLADO ============
    onKeyDown(evt) {
      if (this.readonly) return;
      const target = evt.target;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);
      if (typing) return;

      const ctrl = evt.ctrlKey || evt.metaKey;
      if (ctrl && !evt.shiftKey && evt.key.toLowerCase() === "z") {
        evt.preventDefault();
        this.undo();
        return;
      }
      if ((ctrl && evt.shiftKey && evt.key.toLowerCase() === "z") || (ctrl && evt.key.toLowerCase() === "y")) {
        evt.preventDefault();
        this.redo();
        return;
      }
      if (evt.key === "Escape") {
        // parede em rascunho: descarta sem criar nada
        if (this.activeTool === "utility:wall" && this.$refs.canvas) {
          this.$refs.canvas.cancelWallDraft();
        }
        this.selection = { kind: null, id: null };
        this.activeTool = "select";
        this.closeEdgeMenu();
        this.closeSecondaryMenu();
        this.closePieceMenu();
        this.closeCutoutMenu();
        this.closeAnnotationMenu();
        this.cancelDimensionEdit();
        return;
      }
      if (evt.key === "Enter" && this.activeTool === "utility:wall") {
        // finaliza a parede em rascunho (dblclique faz o mesmo)
        evt.preventDefault();
        if (this.$refs.canvas) this.$refs.canvas.finishWallDraft();
        return;
      }
      if (evt.key === "f" || evt.key === "F") {
        this.centerView();
        return;
      }
      if ((evt.key === "Delete" || evt.key === "Backspace") && this.selection.id) {
        // remove a seleção (peça sai da ficha junto com vinculados; Ctrl+Z desfaz)
        evt.preventDefault();
        this.onRemoveEntity({ ...this.selection });
        return;
      }
      const arrows = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
      if (arrows[evt.key] && this.selection.id) {
        evt.preventDefault();
        const stepMeters = evt.shiftKey ? 0.1 : 0.01; // 10cm | 1cm
        const [nx, ny] = arrows[evt.key];
        this.nudgeSelection(nx * stepMeters, ny * stepMeters);
      }
    },
    // move a seleção por deltas em METROS (setas do teclado)
    nudgeSelection(dxM, dyM) {
      const { kind, id } = this.selection;
      if (kind === "piece") {
        const piece = this.project.pieces.find(p => p.id === id);
        if (!piece) return;
        const dxPct = ((dxM * this.project.metersToPx) / this.canvasWidth) * 100;
        const dyPct = ((dyM * this.project.metersToPx) / this.canvasHeight) * 100;
        this.onMovePiece({ id, dxPct, dyPct });
        return;
      }
      if (kind === "cutout") {
        this.onMoveCutout({ id, dxM, dyM });
        return;
      }
      if (kind === "element" || kind === "annotation" || kind === "image") {
        this.onMoveEntity({
          kind,
          id,
          dx: dxM * this.project.metersToPx,
          dy: dyM * this.project.metersToPx
        });
      }
    },
    setTool(tool) {
      this.activeTool = tool;
      // a ferramenta de imagem dispara o seletor de arquivo no próximo clique
    },

    // ============ SELEÇÃO ============
    onSelect({ kind, id }) {
      this.selection = { kind, id };
      if (["annotation", "element", "image", "cutout"].includes(kind)) {
        this.sideOpen = true;
      }
      if (kind === "piece") {
        const piece = this.project.pieces.find(p => p.id === id);
        if (piece) this.$emit("select-item", piece.sourceIndex);
      }
    },
    openAnnotationEditor(ann) {
      if (!ann) return;
      this.onSelect({ kind: "annotation", id: ann.id });
      this.sideOpen = true;
    },
    pieceListCode(piece) {
      return `${piece.isPrimary ? "P" : "S"}${piece.sourceIndex + 1}`;
    },

    // ============ PEÇAS (fonte: items) ============
    // Substitui o item de um índice e emite eventos de persistência.
    commitItem(sourceIndex, nextItem) {
      this.pushHistory(`items-${sourceIndex}`);
      const next = this.items.map((item, i) =>
        i === sourceIndex ? nextItem : item
      );
      // evento granular (parecido com v-model item a item)
      this.$emit("update-item", { index: sourceIndex, item: nextItem });
      // array completo (para quem prefere atualizar tudo)
      this.$emit("update:items", next);
      this.$emit("input", next);
    },
    // Aplica um patch ao item original e emite eventos de persistência.
    applyPiecePatch(sourceIndex, patch) {
      if (this.readonly) return;
      const item = this.items[sourceIndex];
      if (!item) return;
      const normalizedPatch = { ...patch };
      const relation =
        normalizedPatch.relacaoPrincipal != null
          ? normalizedPatch.relacaoPrincipal
          : normalizedPatch.relacao_principal;
      if (relation != null) {
        normalizedPatch.relacaoPrincipal = relation;
        normalizedPatch.relacao_principal = relation;
      }
      this.commitItem(sourceIndex, { ...item, ...normalizedPatch });
    },
    // Mudança de MEDIDA vinda do desenho (alças ou edição de cota):
    // aplica dimensões + posição e recalcula o financeiro se o pai injetou
    // um recalculador. Emite 'piece-resized' com before/after para o pai
    // exibir aviso de preço com Desfazer.
    applyPieceDimensions(sourceIndex, { width, height, position }) {
      if (this.readonly) return;
      const item = this.items[sourceIndex];
      if (!item) return;
      const before = { ...item };
      let next = {
        ...item,
        comprimento: width,
        largura: height,
        position: {
          x: position.x,
          y: position.y,
          z: (item.position && item.position.z) || 0,
          basisWidth: this.canvasWidth,
          basisHeight: this.canvasHeight
        }
      };
      if (typeof this.itemRecalculator === "function") {
        next = this.itemRecalculator(next) || next;
      }
      this.commitItem(sourceIndex, next);
      this.$emit("piece-resized", { sourceIndex, before, after: next });
    },
    onResizePiece({ id, width, height, position }) {
      const piece = this.project.pieces.find(p => p.id === id);
      if (!piece || !piece.isStone) return;
      const snapped = this.snapLinkedResize(piece, { width, height, position });
      this.applyPieceDimensions(piece.sourceIndex, snapped || { width, height, position });
    },
    // arraste da alça do vértice interno da silhueta L/U: ajusta o recorte
    onShapeResize({ id, dxM, dyM }) {
      if (this.readonly) return;
      const piece = this.project.pieces.find(p => p.id === id);
      if (!piece || !piece.shape) return;
      if (piece.shape.kind !== "L" && piece.shape.kind !== "U") return;
      const W = piece.width;
      const H = piece.height && piece.height > 0.1 ? piece.height : 0.1;
      const shape = piece.shape;
      const params = { ...(shape.params || {}) };
      const cutW = Number(params.cutW) || W * 0.4;
      const cutH = Number(params.cutH) || H * 0.4;
      const clampW = v => Math.min(Math.max(v, 0.05), W - 0.05);
      const clampH = v => Math.min(Math.max(v, 0.05), H - 0.05);

      if (shape.kind === "L") {
        const corner = params.corner || "tr";
        const signW = corner === "tr" || corner === "br" ? -1 : 1;
        const signH = corner === "tr" || corner === "tl" ? 1 : -1;
        params.cutW = clampW(cutW + signW * dxM);
        params.cutH = clampH(cutH + signH * dyM);
      } else {
        // U: alça no vértice direito do recorte central
        params.cutW = Math.min(clampW(cutW + dxM), W - (Number(params.offset) || 0.05) - 0.05);
        params.cutH = clampH(cutH + dyM);
      }
      this.applyPiecePatch(piece.sourceIndex, {
        shape: { ...shape, params }
      });
    },

    // ============ ACABAMENTO DE BORDA NO CANVAS ============
    pieceById(pieceId) {
      return this.project.pieces.find(p => p.id === pieceId) || null;
    },
    // commit em BLOCO (várias mudanças de itens de uma vez = 1 undo)
    commitItemsBulk(nextItems, tag) {
      this.pushHistory(tag);
      this.$emit("update:items", nextItems);
      this.$emit("input", nextItems);
    },
    finishImage(insumo) {
      if (!insumo) return "";
      if (insumo.imagem) return insumo.imagem;
      if (Array.isArray(insumo.imagens) && insumo.imagens.length) {
        return insumo.imagens[0];
      }
      return "";
    },
    onFinishDragStart(evt, index) {
      evt.dataTransfer.setData("text/plain", `finish:${index}`);
      evt.dataTransfer.effectAllowed = "copy";
    },
    onEdgeDropApply({ pieceId, edge, payload }) {
      if (!`${payload}`.startsWith("finish:")) return;
      const insumo = this.edgeFinishCatalog[parseInt(payload.slice(7), 10)];
      if (insumo) this.applyFinishInsumoToEdge(pieceId, edge, insumo);
    },
    /**
     * Aplica um acabamento CADASTRADO a uma borda:
     *  1. marca a borda no item da peça (edgeFinishes + edgeFinishItems);
     *  2. ADICIONA o item "Acabamento" cobrado na ficha (fórmula intacta,
     *     via itemRecalculator), vinculado à borda por edgeRef.
     * Trocar o acabamento da borda substitui o item cobrado anterior.
     */
    applyFinishInsumoToEdge(pieceId, edge, insumo) {
      if (this.readonly) return;
      if (!insumo || insumo.finish_target === "peca") return;
      const piece = this.pieceById(pieceId);
      if (!piece || !piece.isStone) return;
      const parentItem = this.items[piece.sourceIndex];
      if (!parentItem) return;
      const parentId = parentItem.fichaId || uid("ficha");

      const edges = Array.isArray(parentItem.edgeFinishes)
        ? [...parentItem.edgeFinishes]
        : [];
      if (!edges.includes(edge)) edges.push(edge);
      const patchedParent = {
        ...parentItem,
        fichaId: parentId,
        edgeFinishes: edges,
        edgeFinishItems: {
          ...(parentItem.edgeFinishItems || {}),
          [edge]: {
            insumoId: insumo._id || null,
            nome: insumo.nome,
            imagem: this.finishImage(insumo),
            size: Number((parentItem.edgeFinishItems || {})[edge] && (parentItem.edgeFinishItems || {})[edge].size) || 28,
            offsetX: Number((parentItem.edgeFinishItems || {})[edge] && (parentItem.edgeFinishItems || {})[edge].offsetX) || 0,
            offsetY: Number((parentItem.edgeFinishItems || {})[edge] && (parentItem.edgeFinishItems || {})[edge].offsetY) || 0,
            labelRotation: Number((parentItem.edgeFinishItems || {})[edge] && (parentItem.edgeFinishItems || {})[edge].labelRotation) || 0
          }
        }
      };

      let charged = {
        comprimento: 0,
        largura: 0,
        preco: 0,
        descricao: insumo.nome,
        calculo: 0,
        taxa: 0,
        type: "Acabamento",
        finish_target: "borda",
        total: "0.00",
        quantity: 1,
        ultimo_preco_de_compra: insumo.ultimo_preco_de_compra,
        position: {
          x: 50,
          y: 50,
          z: 0,
          basisWidth: this.canvasWidth,
          basisHeight: this.canvasHeight
        },
        fichaId: uid("ficha"),
        edgeRef: { parentId, edge }
      };
      if (typeof this.itemRecalculator === "function") {
        charged = this.itemRecalculator(charged) || charged;
      }

      const next = this.items
        .map((item, i) => (i === piece.sourceIndex ? patchedParent : item))
        // trocar acabamento: remove o item cobrado anterior desta borda
        .filter(
          item =>
            !(
              item.edgeRef &&
              item.edgeRef.parentId === parentId &&
              item.edgeRef.edge === edge &&
              item !== patchedParent
            )
        )
        .concat([charged]);
      this.commitItemsBulk(next, `finish-${pieceId}-${edge}`);
      this.$emit("notify", {
        type: "success",
        message: `${insumo.nome} aplicado à borda — adicionado ao orçamento.`
      });
    },
    applySurfaceFinishFromMenu(insumo) {
      const piece = this.pieceMenuPiece;
      if (!piece || !insumo || insumo.finish_target !== "peca" || this.readonly) return;
      const parentItem = this.items[piece.sourceIndex];
      if (!parentItem) return;
      const parentId = parentItem.fichaId || uid("ficha");
      const patchedParent = {
        ...parentItem,
        fichaId: parentId,
        surfaceFinishItem: {
          insumoId: insumo._id || null,
          nome: insumo.nome,
          imagem: this.finishImage(insumo)
        }
      };
      let charged = {
        comprimento: 0,
        largura: 0,
        preco: 0,
        descricao: insumo.nome,
        calculo: 0,
        taxa: 0,
        type: "Acabamento",
        finish_target: "peca",
        total: "0.00",
        quantity: 1,
        ultimo_preco_de_compra: insumo.ultimo_preco_de_compra,
        position: {
          x: 50,
          y: 50,
          z: 0,
          basisWidth: this.canvasWidth,
          basisHeight: this.canvasHeight
        },
        fichaId: uid("ficha"),
        surfaceRef: { parentId }
      };
      if (typeof this.itemRecalculator === "function") {
        charged = this.itemRecalculator(charged) || charged;
      }
      const next = this.items
        .map((item, index) => (index === piece.sourceIndex ? patchedParent : item))
        .filter(item => !(item.surfaceRef && item.surfaceRef.parentId === parentId))
        .concat([charged]);
      this.commitItemsBulk(next, `surface-finish-${piece.id}`);
      this.closePieceMenu();
      this.$emit("notify", {
        type: "success",
        message: `${insumo.nome} aplicado à superfície — adicionado ao orçamento.`
      });
    },
    removeSurfaceFinishFromMenu() {
      const piece = this.pieceMenuPiece;
      if (!piece || this.readonly) return;
      const parentItem = this.items[piece.sourceIndex];
      if (!parentItem) return;
      const parentId = parentItem.fichaId;
      const patchedParent = { ...parentItem, surfaceFinishItem: null };
      const next = this.items
        .map((item, index) => (index === piece.sourceIndex ? patchedParent : item))
        .filter(item => !(parentId && item.surfaceRef && item.surfaceRef.parentId === parentId));
      this.commitItemsBulk(next, `surface-finish-remove-${piece.id}`);
      this.closePieceMenu();
    },
    /** Redimensiona o swatch (imagem) do acabamento na borda. */
    onResizeSwatch({ pieceId, edge, delta }) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!piece) return;
      const item = this.items[piece.sourceIndex];
      const info = item && item.edgeFinishItems && item.edgeFinishItems[edge];
      if (!info) return;
      const size = Math.max(
        16,
        Math.min(200, (Number(info.size) || 28) + delta)
      );
      this.applyPiecePatch(piece.sourceIndex, {
        edgeFinishItems: {
          ...item.edgeFinishItems,
          [edge]: { ...info, size: Math.round(size) }
        }
      });
    },
    onMoveSwatch({ pieceId, edge, dx, dy }) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!piece) return;
      const item = this.items[piece.sourceIndex];
      const info = item && item.edgeFinishItems && item.edgeFinishItems[edge];
      if (!info) return;
      this.applyPiecePatch(piece.sourceIndex, {
        edgeFinishItems: {
          ...item.edgeFinishItems,
          [edge]: {
            ...info,
            offsetX: (Number(info.offsetX) || 0) + dx,
            offsetY: (Number(info.offsetY) || 0) + dy
          }
        }
      });
    },
    setEdgeFinishTextRotation(rotation) {
      const piece = this.edgeMenuPiece;
      if (!piece) return;
      const item = this.items[piece.sourceIndex];
      const edge = this.edgeMenu.edge;
      const info = item && item.edgeFinishItems && item.edgeFinishItems[edge];
      if (!info) return;
      this.applyPiecePatch(piece.sourceIndex, {
        edgeFinishItems: {
          ...item.edgeFinishItems,
          [edge]: { ...info, labelRotation: Number(rotation) || 0 }
        }
      });
    },

    /** Remove o acabamento da borda E o item cobrado vinculado a ela. */
    removeFinishFromEdge(pieceId, edge) {
      const piece = this.pieceById(pieceId);
      if (!piece) return;
      const parentItem = this.items[piece.sourceIndex];
      if (!parentItem) return;
      const parentId = parentItem.fichaId;
      const edgeFinishItems = { ...(parentItem.edgeFinishItems || {}) };
      delete edgeFinishItems[edge];
      const patchedParent = {
        ...parentItem,
        edgeFinishes: (parentItem.edgeFinishes || []).filter(e => e !== edge),
        edgeFinishItems
      };
      const next = this.items
        .map((item, i) => (i === piece.sourceIndex ? patchedParent : item))
        .filter(
          item =>
            !(
              parentId &&
              item.edgeRef &&
              item.edgeRef.parentId === parentId &&
              item.edgeRef.edge === edge
            )
        );
      this.commitItemsBulk(next, `finish-remove-${pieceId}-${edge}`);
    },
    // clique na borda: abre o menu de acabamentos cadastrados (o próprio
    // menu orienta o cadastro quando o catálogo está vazio)
    onEdgeClick({ pieceId, edge, clientX, clientY }) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!piece || !piece.isStone) return;
      this.onEdgeMenu({ pieceId, edge, clientX, clientY });
    },
    applyEdgeMenuInsumo(insumo) {
      const { pieceId, edge } = this.edgeMenu;
      this.closeEdgeMenu();
      this.applyFinishInsumoToEdge(pieceId, edge, insumo);
    },

    // ============ BORRACHA (apagar partes da peça) ============
    /**
     * Corta da peça a área do retângulo arrastado com a borracha.
     * O recorte vira um "notch" em METROS relativo à peça
     * (shape { kind: 'custom', notches: [...] }) — o contorno é recalculado
     * pelo engine (traceNotchedOutline). Formatos L/U legados migram para
     * notches equivalentes no primeiro uso da borracha.
     */
    onEraseRect(rect) {
      if (this.readonly) return;
      // peça com maior área de interseção com o retângulo
      const pieces = getRenderablePieces(this.project.pieces);
      let target = null;
      let targetBounds = null;
      let bestArea = 0;
      pieces.forEach(piece => {
        const b = getPieceBounds(piece, this.boundsCtx());
        const ix = Math.max(0, Math.min(rect.x + rect.w, b.x + b.w) - Math.max(rect.x, b.x));
        const iy = Math.max(0, Math.min(rect.y + rect.h, b.y + b.h) - Math.max(rect.y, b.y));
        const area = ix * iy;
        if (area > bestArea) {
          bestArea = area;
          target = piece;
          targetBounds = b;
        }
      });
      if (!target) {
        this.$emit("notify", {
          type: "info",
          message: "Passe a borracha sobre uma peça para recortá-la."
        });
        return;
      }

      const W = target.width;
      const H = target.height && target.height > 0.1 ? target.height : 0.1;
      const b = targetBounds;
      const snap = v => Math.round(v * 1000) / 1000; // 1mm
      // retângulo -> metros relativos à peça (interseção com os bounds)
      let nx = snap(Math.max(0, ((rect.x - b.x) / b.w) * W));
      let ny = snap(Math.max(0, ((rect.y - b.y) / b.h) * H));
      let nw = snap(Math.min(W, ((rect.x + rect.w - b.x) / b.w) * W) - nx);
      let nh = snap(Math.min(H, ((rect.y + rect.h - b.y) / b.h) * H) - ny);
      if (nw < 0.02 || nh < 0.02) return;

      // recorte de pedra parte da BORDA: gruda o notch na borda mais próxima
      const eps = 0.001;
      const touches =
        nx < eps || ny < eps || nx + nw > W - eps || ny + nh > H - eps;
      if (!touches) {
        const gaps = [
          { side: "top", gap: ny },
          { side: "bottom", gap: H - (ny + nh) },
          { side: "left", gap: nx },
          { side: "right", gap: W - (nx + nw) }
        ].sort((a, g) => a.gap - g.gap);
        const nearest = gaps[0].side;
        if (nearest === "top") {
          nh += ny;
          ny = 0;
        } else if (nearest === "bottom") {
          nh = H - ny;
        } else if (nearest === "left") {
          nw += nx;
          nx = 0;
        } else {
          nw = W - nx;
        }
      }
      // não deixa a borracha engolir a peça inteira
      if (nw >= W - 0.02 && nh >= H - 0.02) {
        this.$emit("notify", {
          type: "info",
          message: "O recorte apagaria a peça inteira — reduza a área."
        });
        return;
      }

      // migra L/U legado para notches equivalentes
      const item = this.items[target.sourceIndex];
      const shape = item.shape || null;
      let notches = [];
      if (shape && shape.kind === "custom" && Array.isArray(shape.notches)) {
        notches = [...shape.notches];
      } else if (shape && (shape.kind === "L" || shape.kind === "U")) {
        notches = this.legacyShapeToNotches(shape, W, H);
      }
      notches.push({ x: nx, y: ny, w: nw, h: nh });

      this.applyPiecePatch(target.sourceIndex, {
        shape: { kind: "custom", notches }
      });
    },
    legacyShapeToNotches(shape, W, H) {
      const params = shape.params || {};
      const cutW = Math.min(Math.max(Number(params.cutW) || W * 0.4, 0.05), W - 0.05);
      const cutH = Math.min(Math.max(Number(params.cutH) || H * 0.4, 0.05), H - 0.05);
      if (shape.kind === "L") {
        const corner = params.corner || "tr";
        if (corner === "tr") return [{ x: W - cutW, y: 0, w: cutW, h: cutH }];
        if (corner === "tl") return [{ x: 0, y: 0, w: cutW, h: cutH }];
        if (corner === "br") return [{ x: W - cutW, y: H - cutH, w: cutW, h: cutH }];
        return [{ x: 0, y: H - cutH, w: cutW, h: cutH }];
      }
      // U
      const rawOffset =
        params.offset != null ? Number(params.offset) : (W - cutW) / 2;
      const offset = Math.min(Math.max(rawOffset, 0.05), W - cutW - 0.05);
      return [{ x: offset, y: 0, w: cutW, h: cutH }];
    },

    // ============ COPILOT DAS BORDAS ============
    /**
     * Clique numa sugestão-fantasma: abre o menu COMBINADO no ponto do
     * clique — Peças Secundárias e Acabamentos cadastrados, para QUALQUER
     * peça de pedra (a hierarquia da montagem fica em aberto).
     */
    onAcceptSecondary({ pieceId, edge, clientX, clientY }) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!piece) return;
      if (!this.secondaryCatalog.length && !this.edgeFinishCatalog.length) return;
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      this.closeCutoutMenu();
      this.closeAnnotationMenu();
      this.secondaryMenu = {
        visible: true,
        pieceId,
        edge,
        left: Math.max(4, (clientX || host.left) - host.left - 10),
        top: Math.max(4, (clientY || host.top) - host.top + 6)
      };
    },
    closeSecondaryMenu() {
      this.secondaryMenu = { ...this.secondaryMenu, visible: false };
    },
    onPieceMenu({ pieceId, clientX, clientY }) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!piece || !piece.isStone) return;
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      this.closeEdgeMenu();
      this.closeSecondaryMenu();
      this.closeCutoutMenu();
      this.closeAnnotationMenu();
      this.pieceMenu = {
        visible: true,
        pieceId,
        left: Math.max(4, clientX - host.left - 10),
        top: Math.max(4, clientY - host.top + 6)
      };
    },
    closePieceMenu() {
      this.pieceMenu = { ...this.pieceMenu, visible: false };
    },
    // menu contextual do TEXTO livre (board): mesma rotação em passos de 90°
    // usada no "Texto" da peça, mais editar/remover linha de chamada/excluir
    onAnnotationMenu({ id, clientX, clientY }) {
      if (this.readonly) return;
      const ann = this.findEntity("annotation", id);
      if (!ann) return;
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      this.closeEdgeMenu();
      this.closeSecondaryMenu();
      this.closeCutoutMenu();
      this.closePieceMenu();
      this.selection = { kind: "annotation", id };
      this.annotationMenu = {
        visible: true,
        id,
        left: Math.max(4, clientX - host.left - 10),
        top: Math.max(4, clientY - host.top + 6)
      };
    },
    closeAnnotationMenu() {
      this.annotationMenu = { ...this.annotationMenu, visible: false };
    },
    rotateAnnotationFromMenu(delta) {
      const ann = this.findEntity("annotation", this.annotationMenu.id);
      if (!ann) return;
      const current = Number(ann.rotation) || 0;
      const next = (((current + delta) % 360) + 360) % 360;
      this.onUpdateEntity("annotation", ann.id, { rotation: next });
    },
    toggleAnnotationLeaderFromMenu() {
      const ann = this.findEntity("annotation", this.annotationMenu.id);
      if (!ann) return;
      const leader = ann.leader
        ? null
        : { x: ann.x + 60, y: ann.y + 40 };
      this.onUpdateEntity("annotation", ann.id, { leader });
    },
    editAnnotationFromMenu() {
      const id = this.annotationMenu.id;
      this.closeAnnotationMenu();
      const ann = this.findEntity("annotation", id);
      if (ann) this.openAnnotationEditor(ann);
    },
    removeAnnotationFromMenu() {
      const id = this.annotationMenu.id;
      this.closeAnnotationMenu();
      if (id) this.onRemoveEntity({ kind: "annotation", id });
    },
    onCutoutMenu({ id, clientX, clientY }) {
      if (this.readonly) return;
      const found = this.findCutout(id);
      if (!found) return;
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      this.closeEdgeMenu();
      this.closeSecondaryMenu();
      this.closePieceMenu();
      this.closeAnnotationMenu();
      this.selection = { kind: "cutout", id };
      this.cutoutMenu = {
        visible: true,
        id,
        left: Math.max(4, clientX - host.left - 10),
        top: Math.max(4, clientY - host.top + 6)
      };
    },
    closeCutoutMenu() {
      this.cutoutMenu = { ...this.cutoutMenu, visible: false };
    },
    openCutoutProperties() {
      this.sideOpen = true;
      this.closeCutoutMenu();
    },
    removeCutoutFromMenu() {
      const id = this.cutoutMenu.id;
      this.closeCutoutMenu();
      if (id) this.removeCutout(id);
    },
    rotationSteps(piece) {
      const angle = (((Number(piece && piece.rotation) || 0) % 360) + 360) % 360;
      return Math.round(angle / 90) % 4;
    },
    rotateSide(side, steps) {
      const sides = ["top", "right", "bottom", "left"];
      const index = sides.indexOf(side);
      return index < 0 ? side : sides[(index + steps + 4) % 4];
    },
    localSideForVisualSide(visualSide) {
      const piece = this.pieceMenuPiece;
      const steps = this.rotationSteps(piece);
      return ["top", "right", "bottom", "left"].find(
        side => this.rotateSide(side, steps) === visualSide
      ) || visualSide;
    },
    dimensionForLocalSide(localSide) {
      if (localSide === "top" || localSide === "bottom") {
        return { key: "lengthSide", value: localSide };
      }
      return { key: "widthSide", value: localSide };
    },
    visualRulerOption(visualSide) {
      const localSide = this.localSideForVisualSide(visualSide);
      const dim = this.dimensionForLocalSide(localSide);
      const current = this.pieceMenuDimSides[dim.key];
      return {
        key: dim.key,
        value: dim.value,
        visible: current !== "off",
        active: current === dim.value
      };
    },
    setVisualRulerSide(visualSide) {
      const option = this.visualRulerOption(visualSide);
      this.setPieceDimensionSide(option.key, option.value);
    },
    toggleVisualRuler(visualSide) {
      const option = this.visualRulerOption(visualSide);
      this.setPieceDimensionSide(
        option.key,
        option.active && option.visible ? "off" : option.value
      );
    },
    setPieceDimensionSide(key, value) {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      const item = this.items[piece.sourceIndex];
      const current = (item && item.dimensionSides) || {};
      this.applyPiecePatch(piece.sourceIndex, {
        dimensionSides: { ...current, [key]: value }
      });
    },
    setPieceTextDirection(value) {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      this.applyPiecePatch(piece.sourceIndex, { labelTextDirection: value });
    },
    rotatePieceTextDirection(delta) {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      const current = Number(piece.labelTextDirection) || 0;
      const next = (((current + delta) % 360) + 360) % 360;
      this.applyPiecePatch(piece.sourceIndex, { labelTextDirection: next });
    },
    rotatePieceFromMenu(delta) {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      const nextRotation = (((piece.rotation || 0) + delta) % 360 + 360) % 360;
      this.applyPiecePatch(piece.sourceIndex, { rotation: nextRotation });
    },
    movePieceLayerFromMenu(direction) {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      const stoneIndexes = this.project.pieces
        .filter(p => p.isStone)
        .map(p => p.sourceIndex);
      const currentPos = stoneIndexes.indexOf(piece.sourceIndex);
      const targetPos = currentPos + (direction > 0 ? 1 : -1);
      if (currentPos < 0 || targetPos < 0 || targetPos >= stoneIndexes.length) return;
      const targetIndex = stoneIndexes[targetPos];
      const next = [...this.items];
      const selectedItem = next[piece.sourceIndex];
      next[piece.sourceIndex] = next[targetIndex];
      next[targetIndex] = selectedItem;
      this.commitItemsBulk(next, `layer-piece-${piece.id}`);
      this.$nextTick(() => {
        this.selection = {
          kind: "piece",
          id: `piece_${String(targetIndex + 1).padStart(3, "0")}`
        };
        this.pieceMenu = {
          ...this.pieceMenu,
          pieceId: `piece_${String(targetIndex + 1).padStart(3, "0")}`
        };
      });
    },
    duplicatePieceFromMenu() {
      const piece = this.pieceMenuPiece;
      if (!piece || !piece.isStone) return;
      const original = this.items[piece.sourceIndex];
      if (!original) return;
      const pos = original.position || { x: 50, y: 50, z: 0 };
      let duplicate = JSON.parse(JSON.stringify(original));
      delete duplicate._id;
      delete duplicate.id;
      delete duplicate.edgeRef;
      delete duplicate.linkedTo;
      duplicate.fichaId = uid("ficha");
      duplicate.position = {
        x: (Number(pos.x) || 0) + 6,
        y: (Number(pos.y) || 0) + 6,
        z: pos.z || 0,
        basisWidth: this.canvasWidth,
        basisHeight: this.canvasHeight
      };
      if (typeof this.itemRecalculator === "function") {
        duplicate = this.itemRecalculator(duplicate) || duplicate;
      }
      const next = this.items.concat([duplicate]);
      this.commitItemsBulk(next, `duplicate-piece-${piece.id}`);
      this.closePieceMenu();
      this.$nextTick(() => {
        this.selection = { kind: "piece", id: `piece_${String(next.length).padStart(3, "0")}` };
        this.centerView();
      });
      this.$emit("notify", {
        type: "success",
        message: `${duplicate.descricao || duplicate.type || "Peça"} duplicada.`
      });
    },
    removePieceFromMenu() {
      const piece = this.pieceMenuPiece;
      if (!piece) return;
      this.closePieceMenu();
      this.removeFichaItemByIndex(piece.sourceIndex);
    },
    applySecondaryMenuInsumo(insumo) {
      const { pieceId, edge } = this.secondaryMenu;
      this.closeSecondaryMenu();
      this.addSecondaryPiece(insumo, pieceId, edge);
    },
    // opção "Acabamento" do menu combinado: aplica na borda clicada
    applySecondaryMenuFinish(insumo) {
      const { pieceId, edge } = this.secondaryMenu;
      this.closeSecondaryMenu();
      this.applyFinishInsumoToEdge(pieceId, edge, insumo);
    },
    /**
     * Cria o item "Peça Secundária" já na medida da borda (comprimento =
     * borda, largura = 10cm), vinculado à peça-mãe (linkedTo) e paralelo
     * ao lado clicado. Preço via itemRecalculator.
     */
    addSecondaryPiece(insumo, pieceId, edge) {
      if (this.readonly) return;
      const piece = this.pieceById(pieceId);
      if (!insumo || !piece) return;
      const parentItem = this.items[piece.sourceIndex];
      if (!parentItem) return;
      const parentId = parentItem.fichaId || uid("ficha");
      const edgeLen =
        edge === "front" || edge === "back" ? piece.width : piece.height;

      // posição adjacente e PARALELA à borda clicada (em % do canvas);
      // saias laterais nascem giradas 90° para acompanhar o lado.
      // Folga: centro da secundária = borda + meia espessura visual + respiro.
      const b = getPieceBounds(piece, this.boundsCtx());
      const stripHalf = Math.max(0.1 * this.project.metersToPx, 8) / 2;
      const parentRotation = Number(piece.rotation) || 0;
      const distance = stripHalf;
      const edgeAnchors = {
        front: { x: b.cx, y: b.y + b.h, nx: 0, ny: 1, rotation: 0 },
        back: { x: b.cx, y: b.y, nx: 0, ny: -1, rotation: 0 },
        left: { x: b.x, y: b.cy, nx: -1, ny: 0, rotation: 90 },
        right: { x: b.x + b.w, y: b.cy, nx: 1, ny: 0, rotation: 90 }
      };
      const anchor = edgeAnchors[edge] || edgeAnchors.front;
      const rad = (parentRotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const dx = anchor.x - b.cx;
      const dy = anchor.y - b.cy;
      const rotatedX = b.cx + dx * cos - dy * sin;
      const rotatedY = b.cy + dx * sin + dy * cos;
      const normalX = anchor.nx * cos - anchor.ny * sin;
      const normalY = anchor.nx * sin + anchor.ny * cos;
      const at = {
        x: rotatedX + normalX * distance,
        y: rotatedY + normalY * distance,
        rotation: (((anchor.rotation + parentRotation) % 360) + 360) % 360
      };
      const toPct = (value, size) => (value / size) * 100;

      const relacaoPrincipal =
        insumo.relacao_principal || insumo.relacaoPrincipal || "neutra";
      const parentThicknessMm =
        parentItem.slabThicknessMm ||
        parentItem.espessura_mm ||
        parentItem.espessuraMm ||
        10;
      let saia = {
        comprimento: Math.round(edgeLen * 1000) / 1000,
        largura: 0.1,
        preco: 0,
        descricao: insumo.nome,
        calculo: 0,
        taxa: 0,
        type: insumo.type || "Peça Secundária",
        total: "0.00",
        quantity: 1,
        edgeFinishType: "polida",
        edgeFinishes: [],
        cutouts: [],
        edgeLabels: {},
        rotation: at.rotation || 0,
        fichaId: uid("ficha"),
        // relação com a principal vem do CADASTRO do insumo (saia = curvada
        // para baixo etc.) — define como a perspectiva desenha a peça
        relacaoPrincipal,
        relacao_principal: relacaoPrincipal,
        slabThicknessMm: parentThicknessMm,
        espessura_mm: parentThicknessMm,
        espessuraMm: parentThicknessMm,
        linkedTo: { parentId, edge },
        position: {
          x: toPct(at.x, this.canvasWidth),
          y: toPct(at.y, this.canvasHeight),
          z: 0,
          basisWidth: this.canvasWidth,
          basisHeight: this.canvasHeight
        }
      };
      if (typeof this.itemRecalculator === "function") {
        saia = this.itemRecalculator(saia) || saia;
      }

      const next = this.items
        .map((item, i) =>
          i === piece.sourceIndex ? { ...item, fichaId: parentId } : item
        )
        .concat([saia]);
      this.commitItemsBulk(next, `secondary-${pieceId}-${edge}`);
      this.$nextTick(() => this.centerView());
      this.$emit("notify", {
        type: "success",
        message: `${insumo.nome} (${formatMeasure(saia.comprimento)}m) adicionado à peça.`
      });
    },
    // clique direito na borda: abre o menu de tipos ancorado no ponto
    onEdgeMenu({ pieceId, edge, clientX, clientY }) {
      if (this.readonly) return;
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      this.closeCutoutMenu();
      this.closePieceMenu();
      this.closeSecondaryMenu();
      this.closeAnnotationMenu();
      this.edgeMenu = {
        visible: true,
        pieceId,
        edge,
        left: Math.max(4, clientX - host.left - 10),
        top: Math.max(4, clientY - host.top + 6)
      };
    },
    closeEdgeMenu() {
      this.edgeMenu = { ...this.edgeMenu, visible: false };
    },
    removeEdgeMenuEdge() {
      const { pieceId, edge } = this.edgeMenu;
      this.closeEdgeMenu();
      // remove a marcação visual E o item "Acabamento" cobrado desta borda
      this.removeFinishFromEdge(pieceId, edge);
    },

    // ============ EDIÇÃO INLINE DE COTA ============
    onEditDimension({ pieceId, axis, x, y }) {
      if (this.readonly) return;
      const piece = this.project.pieces.find(p => p.id === pieceId);
      if (!piece) return;
      const canvas = this.$refs.canvas;
      const client = canvas ? canvas.contentToClient(x, y) : { x: 0, y: 0 };
      const stage = this.$refs.stage;
      const host = (stage || this.$el).getBoundingClientRect();
      const current = axis === "length" ? piece.width : piece.height;
      this.dimEditor = {
        visible: true,
        pieceId,
        axis,
        value: formatMeasure(current, ","),
        left: client.x - host.left - 34,
        top: client.y - host.top - 14
      };
      this.$nextTick(() => {
        if (this.$refs.dimInput) {
          this.$refs.dimInput.focus();
          this.$refs.dimInput.select();
        }
      });
    },
    confirmDimensionEdit() {
      if (!this.dimEditor.visible) return;
      const { pieceId, axis, value } = this.dimEditor;
      this.dimEditor = { ...this.dimEditor, visible: false };
      const piece = this.project.pieces.find(p => p.id === pieceId);
      if (!piece) return;
      const meters = parseFloat(`${value}`.replace(",", "."));
      if (!(meters > 0)) return;
      const snapped = Math.max(0.02, Math.round(meters * 1000) / 1000);
      this.applyPieceDimensions(piece.sourceIndex, {
        width: axis === "length" ? snapped : piece.width,
        height: axis === "width" ? snapped : piece.height,
        position: { x: piece.position.x, y: piece.position.y }
      });
    },
    cancelDimensionEdit() {
      if (!this.dimEditor.visible) return;
      this.dimEditor = { ...this.dimEditor, visible: false };
    },
    edgeFrame(parent, edge) {
      if (!parent) return null;
      const parentBounds = getPieceBounds(parent, this.boundsCtx());
      const parentRotation = Number(parent.rotation) || 0;
      const rad = (parentRotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const edgeAnchors = {
        front: { x: parentBounds.cx, y: parentBounds.y + parentBounds.h, nx: 0, ny: 1, tx: 1, ty: 0, rotation: 0 },
        back: { x: parentBounds.cx, y: parentBounds.y, nx: 0, ny: -1, tx: 1, ty: 0, rotation: 0 },
        left: { x: parentBounds.x, y: parentBounds.cy, nx: -1, ny: 0, tx: 0, ty: 1, rotation: 90 },
        right: { x: parentBounds.x + parentBounds.w, y: parentBounds.cy, nx: 1, ny: 0, tx: 0, ty: 1, rotation: 90 }
      };
      const anchor = edgeAnchors[edge] || edgeAnchors.front;
      const rotateVec = (x, y) => ({ x: x * cos - y * sin, y: x * sin + y * cos });
      const dx = anchor.x - parentBounds.cx;
      const dy = anchor.y - parentBounds.cy;
      return {
        anchor,
        bounds: parentBounds,
        normal: rotateVec(anchor.nx, anchor.ny),
        tangent: rotateVec(anchor.tx, anchor.ty),
        edgeHalf:
          (edge === "front" || edge === "back" ? parentBounds.w : parentBounds.h) / 2,
        seam: {
          x: parentBounds.cx + dx * cos - dy * sin,
          y: parentBounds.cy + dx * sin + dy * cos
        },
        rotation: (((anchor.rotation + parentRotation) % 360) + 360) % 360
      };
    },
    pieceLongHalfPx(piece) {
      return Math.max((Number(piece && piece.width) || 0) * this.project.metersToPx, 8) / 2;
    },
    snapSlideToGuides(slide, parentHalf, childHalf, threshold = 12) {
      const targets = [0];
      if (Number.isFinite(parentHalf) && Number.isFinite(childHalf)) {
        targets.push(parentHalf - childHalf);
        targets.push(-parentHalf + childHalf);
      }
      let best = slide;
      let bestDistance = threshold + 1;
      targets.forEach(target => {
        const distance = Math.abs(slide - target);
        if (distance <= threshold && distance < bestDistance) {
          best = target;
          bestDistance = distance;
        }
      });
      return best;
    },
    snapLinkedResize(piece, next) {
      if (!piece || !piece.linkedTo || !piece.linkedTo.parentId) return null;
      // sobreposta acima/abaixo (sem edge): não força o redimensionamento
      // de volta para uma borda — a peça é livre por cima/por baixo da
      // principal (mesmo raciocínio de linkedPiecePosition).
      if (!piece.linkedTo.edge) return null;
      const parent = this.project.pieces.find(p => p.fichaId === piece.linkedTo.parentId);
      if (!parent) return null;
      const edge = piece.linkedTo.edge;
      const frame = this.edgeFrame(parent, edge);
      if (!frame) return null;
      const resizedPiece = {
        ...piece,
        width: next.width,
        height: next.height,
        position: next.position
      };
      const childBounds = getPieceBounds(resizedPiece, this.boundsCtx());
      const vx = childBounds.cx - frame.seam.x;
      const vy = childBounds.cy - frame.seam.y;
      const along = vx * frame.tangent.x + vy * frame.tangent.y;
      const childHalf = this.pieceLongHalfPx(resizedPiece);
      const snappedSlide = this.snapSlideToGuides(along, frame.edgeHalf, childHalf, 14);
      if (Math.abs(snappedSlide - along) > 14) return null;

      const contactDistance = Math.max(childBounds.h, 8) / 2;
      const snappedCenter = {
        x:
          frame.seam.x +
          frame.tangent.x * snappedSlide +
          frame.normal.x * contactDistance,
        y:
          frame.seam.y +
          frame.tangent.y * snappedSlide +
          frame.normal.y * contactDistance
      };
      let width = next.width;
      // Quando a peça fica quase do tamanho exato da borda, fecha a medida
      // para formar uma linha única de ponta a ponta.
      const edgeMeters = edge === "front" || edge === "back" ? parent.width : parent.height;
      if (Math.abs(width - edgeMeters) * this.project.metersToPx <= 14) {
        width = Math.round(edgeMeters * 1000) / 1000;
      }
      return {
        width,
        height: next.height,
        position: {
          x: (snappedCenter.x / this.canvasWidth) * 100,
          y: (snappedCenter.y / this.canvasHeight) * 100
        }
      };
    },
    linkedPiecePosition(piece, dxPct, dyPct) {
      if (!piece || !piece.linkedTo || !piece.linkedTo.parentId) return null;
      // sem edge (sobreposta acima/abaixo): a peça não está grudada a uma
      // borda específica — é uma superfície de sustentação que deve poder
      // ser arrastada livremente por cima/por baixo da principal, inclusive
      // sobrepondo a área dela. Sem essa saída, o "|| front" abaixo prendia
      // a peça deslizando ao longo da borda da frente (não deixava "entrar").
      if (!piece.linkedTo.edge) return null;
      const parent = this.project.pieces.find(p => p.fichaId === piece.linkedTo.parentId);
      if (!parent) return null;
      const edge = piece.linkedTo.edge;
      const frame = this.edgeFrame(parent, edge);
      if (!frame) return null;
      const childBounds = getPieceBounds(piece, this.boundsCtx());
      const desired = {
        x: childBounds.cx + (dxPct / 100) * this.canvasWidth,
        y: childBounds.cy + (dyPct / 100) * this.canvasHeight
      };
      const vx = desired.x - frame.seam.x;
      const vy = desired.y - frame.seam.y;
      const childHalf = this.pieceLongHalfPx(piece);
      const rawSlide = vx * frame.tangent.x + vy * frame.tangent.y;
      const snappedSlide = this.snapSlideToGuides(rawSlide, frame.edgeHalf, childHalf);
      const slide = Math.max(-frame.edgeHalf, Math.min(frame.edgeHalf, snappedSlide));
      const contactDistance = Math.max(childBounds.h, 8) / 2;
      const next = {
        x: frame.seam.x + frame.tangent.x * slide + frame.normal.x * contactDistance,
        y: frame.seam.y + frame.tangent.y * slide + frame.normal.y * contactDistance
      };
      return {
        x: (next.x / this.canvasWidth) * 100,
        y: (next.y / this.canvasHeight) * 100
      };
    },
    snapSecondaryToPrimary(piece, dxPct, dyPct) {
      if (!piece || !piece.isSecondary || piece.linkedTo) return null;
      const desiredPiece = {
        ...piece,
        position: {
          ...(piece.position || {}),
          x: (Number(piece.position && piece.position.x) || 0) + dxPct,
          y: (Number(piece.position && piece.position.y) || 0) + dyPct
        }
      };
      const childBounds = getPieceBounds(desiredPiece, this.boundsCtx());
      const snapPx = 18;
      const candidates = [];
      this.project.pieces
        .filter(p => p.isPrimary && p.sourceIndex !== piece.sourceIndex)
        .forEach(parent => {
          ["front", "back", "left", "right"].forEach(edge => {
            const frame = this.edgeFrame(parent, edge);
            if (!frame) return;
            const vx = childBounds.cx - frame.seam.x;
            const vy = childBounds.cy - frame.seam.y;
            const along = vx * frame.tangent.x + vy * frame.tangent.y;
            const perpendicular = vx * frame.normal.x + vy * frame.normal.y;
            const childHalf = this.pieceLongHalfPx(piece);
            const snappedAlong = this.snapSlideToGuides(along, frame.edgeHalf, childHalf);
            const contactDistance = Math.max(childBounds.h, 8) / 2;
            const edgeOverflow = Math.max(0, Math.abs(snappedAlong) + childHalf - frame.edgeHalf);
            const distanceError = Math.abs(perpendicular - contactDistance);
            if (distanceError > snapPx || edgeOverflow > snapPx) return;
            const slide = Math.max(-frame.edgeHalf, Math.min(frame.edgeHalf, snappedAlong));
            const next = {
              x: frame.seam.x + frame.tangent.x * slide + frame.normal.x * contactDistance,
              y: frame.seam.y + frame.tangent.y * slide + frame.normal.y * contactDistance
            };
            candidates.push({
              parent,
              edge,
              score: distanceError + edgeOverflow + Math.abs(snappedAlong - along) * 0.25,
              position: {
                x: (next.x / this.canvasWidth) * 100,
                y: (next.y / this.canvasHeight) * 100
              },
              rotation: frame.rotation,
              comprimento:
                edge === "front" || edge === "back" ? parent.width : parent.height
            });
          });
        });
      if (!candidates.length) return null;
      return candidates.sort((a, b) => a.score - b.score)[0];
    },
    onMovePiece({ id, dxPct, dyPct }) {
      if (this.readonly) return;
      const piece = this.project.pieces.find(p => p.id === id);
      if (!piece) return;
      // peças vinculadas (saia/rodapé com linkedTo) acompanham a peça-mãe
      if (piece.linkedTo && piece.linkedTo.parentId) {
        const linkedPosition = this.linkedPiecePosition(piece, dxPct, dyPct);
        if (linkedPosition) {
          const item = this.items[piece.sourceIndex];
          const next = this.items.map((it, i) =>
            i === piece.sourceIndex
              ? {
                  ...item,
                  position: {
                    x: linkedPosition.x,
                    y: linkedPosition.y,
                    z: (item.position && item.position.z) || 0,
                    basisWidth: this.canvasWidth,
                    basisHeight: this.canvasHeight
                  }
                }
              : it
          );
          this.commitItemsBulk(next, `move-linked-piece-${id}`);
          return;
        }
      }
      const snap = this.snapSecondaryToPrimary(piece, dxPct, dyPct);
      if (snap) {
        const parentItem = this.items[snap.parent.sourceIndex];
        const item = this.items[piece.sourceIndex];
        const parentId =
          (parentItem && parentItem.fichaId) || snap.parent.fichaId || uid("ficha");
        let patchedPiece = {
          ...item,
          comprimento: Math.round(snap.comprimento * 1000) / 1000,
          rotation: snap.rotation,
          linkedTo: { parentId, edge: snap.edge },
          position: {
            x: snap.position.x,
            y: snap.position.y,
            z: (item.position && item.position.z) || 0,
            basisWidth: this.canvasWidth,
            basisHeight: this.canvasHeight
          }
        };
        if (typeof this.itemRecalculator === "function") {
          patchedPiece = this.itemRecalculator(patchedPiece) || patchedPiece;
        }
        const next = this.items.map((it, i) => {
          if (i === snap.parent.sourceIndex) return { ...it, fichaId: parentId };
          if (i === piece.sourceIndex) return patchedPiece;
          return it;
        });
        this.commitItemsBulk(next, `snap-secondary-${id}-${snap.edge}`);
        return;
      }
      const parentId = piece.fichaId;
      const followerIndexes = parentId
        ? this.project.pieces
            .filter(p => p.linkedTo && p.linkedTo.parentId === parentId)
            .map(p => p.sourceIndex)
        : [];
      const moving = [piece.sourceIndex, ...followerIndexes];
      const next = this.items.map((item, i) => {
        if (!moving.includes(i)) return item;
        const pos = (item && item.position) || { x: 50, y: 50, z: 0 };
        // mantém o formato de position PERCENTUAL (compatível com o modal)
        return {
          ...item,
          position: {
            x: (Number(pos.x) || 0) + dxPct,
            y: (Number(pos.y) || 0) + dyPct,
            z: pos.z || 0,
            basisWidth: this.canvasWidth,
            basisHeight: this.canvasHeight
          }
        };
      });
      this.commitItemsBulk(next, `move-piece-${id}`);
    },
    onInspectorPiecePatch(patch) {
      const piece = this.selectedPiece;
      if (!piece) return;
      // edgeFinishType / edgeFinishes vão direto para o item original
      this.applyPiecePatch(piece.sourceIndex, patch);
    },

    // ============ RECORTES VINCULADOS ============
    boundsCtx() {
      return {
        scale: this.project.scale,
        width: this.canvasWidth,
        height: this.canvasHeight
      };
    },
    pieceMetersOf(piece) {
      return {
        w: piece.width || 0,
        h: piece.height && piece.height > 0.1 ? piece.height : 0.1
      };
    },
    // insere um recorte na peça sob o ponto clicado (ou na peça selecionada).
    // `key` pode ser um símbolo padrão ("hole") ou um insumo Furo ("ins-2").
    insertCutoutAt(key, x, y) {
      const pieces = getRenderablePieces(this.project.pieces);
      let target = pieces.find(p => {
        const b = getPieceBounds(p, this.boundsCtx());
        return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
      });
      if (!target && this.selectedPiece && this.selectedPiece.isStone) {
        target = this.selectedPiece;
      }
      if (!target) {
        this.$emit("notify", {
          type: "info",
          message: "Clique sobre uma peça para inserir o recorte."
        });
        return;
      }
      const meters = this.pieceMetersOf(target);
      // Todo recorte nasce no centro geométrico da peça. O clique serve para
      // escolher a peça; depois o atendente posiciona o furo por arraste.
      const cx = meters.w / 2;
      const cy = meters.h / 2;
      // furo cadastrado (insumo) ou símbolo padrão
      let overrides = { cx, cy };
      let kind = key;
      let selectedInsumo = null;
      if (`${key}`.startsWith("ins-")) {
        const insumo = this.cutoutCatalog[parseInt(key.slice(4), 10)];
        if (insumo) {
          selectedInsumo = insumo;
          const spec = cutoutFromInsumo(insumo);
          kind = spec.kind;
          overrides = { ...spec, cx, cy };
          delete overrides.kind;
        }
      }
      const cutout = clampCutoutToPiece(createCutout(kind, overrides), meters);
      const item = this.items[target.sourceIndex];
      const cutouts = Array.isArray(item.cutouts) ? [...item.cutouts, cutout] : [cutout];
      if (!selectedInsumo) {
        this.applyPiecePatch(target.sourceIndex, { cutouts });
      } else {
        const parentId = item.fichaId || uid("ficha");
        const patchedParent = { ...item, fichaId: parentId, cutouts };
        let charged = {
          comprimento: 0,
          largura: 0,
          preco: 0,
          descricao: selectedInsumo.nome,
          calculo: 0,
          taxa: 0,
          type: "Furo",
          total: "0.00",
          quantity: 1,
          ultimo_preco_de_compra: selectedInsumo.ultimo_preco_de_compra,
          fichaId: uid("ficha"),
          cutoutRef: { parentId, cutoutId: cutout.id }
        };
        if (typeof this.itemRecalculator === "function") {
          charged = this.itemRecalculator(charged) || charged;
        }
        const next = this.items
          .map((current, index) => (index === target.sourceIndex ? patchedParent : current))
          .concat([charged]);
        this.commitItemsBulk(next, `cutout-${cutout.id}`);
      }
      this.selection = { kind: "cutout", id: cutout.id };
    },
    findCutout(cutoutId) {
      for (const piece of this.project.pieces) {
        const cutout = (piece.cutouts || []).find(c => c.id === cutoutId);
        if (cutout) return { piece, cutout };
      }
      return null;
    },
    onMoveCutout({ id, dxM, dyM }) {
      if (this.readonly) return;
      const found = this.findCutout(id);
      if (!found) return;
      const meters = this.pieceMetersOf(found.piece);
      const item = this.items[found.piece.sourceIndex];
      const cutouts = (item.cutouts || []).map(c =>
        c.id === id
          ? clampCutoutToPiece({ ...c, cx: c.cx + dxM, cy: c.cy + dyM }, meters)
          : c
      );
      this.applyPiecePatch(found.piece.sourceIndex, { cutouts });
    },
    // resize do recorte pela alça: aplica deltas em METROS com snap de 1mm
    onResizeCutout({ id, dwM, dhM }) {
      if (this.readonly) return;
      const found = this.findCutout(id);
      if (!found) return;
      const meters = this.pieceMetersOf(found.piece);
      const snap = v => Math.round(v * 1000) / 1000;
      const item = this.items[found.piece.sourceIndex];
      const cutouts = (item.cutouts || []).map(c => {
        if (c.id !== id) return c;
        let next;
        if (c.diameter) {
          next = {
            ...c,
            diameter: Math.max(0.02, snap(c.diameter + Math.max(dwM, dhM)))
          };
        } else {
          next = {
            ...c,
            width: Math.max(0.03, snap((c.width || 0.1) + dwM)),
            height: Math.max(0.03, snap((c.height || 0.1) + dhM))
          };
        }
        return clampCutoutToPiece(next, meters);
      });
      this.applyPiecePatch(found.piece.sourceIndex, { cutouts });
    },
    onUpdateCutout(patch) {
      if (this.readonly || this.selection.kind !== "cutout") return;
      const found = this.findCutout(this.selection.id);
      if (!found) return;
      const meters = this.pieceMetersOf(found.piece);
      const item = this.items[found.piece.sourceIndex];
      const cutouts = (item.cutouts || []).map(c =>
        c.id === this.selection.id
          ? clampCutoutToPiece({ ...c, ...patch }, meters)
          : c
      );
      this.applyPiecePatch(found.piece.sourceIndex, { cutouts });
    },
    removeCutout(cutoutId) {
      const found = this.findCutout(cutoutId);
      if (!found) return;
      const item = this.items[found.piece.sourceIndex];
      const cutouts = (item.cutouts || []).filter(c => c.id !== cutoutId);
      const next = this.items
        .map((current, index) =>
          index === found.piece.sourceIndex ? { ...current, cutouts } : current
        )
        .filter(current => !(current.cutoutRef && current.cutoutRef.cutoutId === cutoutId));
      this.commitItemsBulk(next, `cutout-remove-${cutoutId}`);
      if (this.selection.id === cutoutId) {
        this.selection = { kind: null, id: null };
      }
    },

    // ============ CAMADAS LIVRES (elementos / anotações / imagens) ============
    onCanvasInsert({ tool, x, y }) {
      if (this.readonly) return;
      if (`${tool}`.startsWith("cutout:")) {
        this.insertCutoutAt(tool.slice(7), x, y);
        this.activeTool = "select";
        return;
      }
      if (tool === "annotation") {
        this.addEntity("annotation", createAnnotation({ x, y }));
      } else if (tool === "image") {
        // guarda o ponto e abre o seletor de arquivo
        this.pendingImagePoint = { x, y };
        this.$refs.fileInput && this.$refs.fileInput.click();
      } else {
        // qualquer ferramenta de elemento técnico (line, arrow, cutout_*, hole...)
        const meta = getElementMeta(tool);
        const overrides = { x, y };
        if (meta.shape === "circle") { overrides.width = 40; overrides.height = 40; }
        if (meta.shape === "line") { overrides.width = 80; overrides.height = 0; }
        this.addEntity("element", createDrawingElement(tool, overrides));
      }
      // volta para seleção após inserir um item
      this.activeTool = "select";
    },
    addEntity(kind, entity) {
      this.pushHistory(`add-${kind}-${entity.id}`);
      const list = this.listFor(kind);
      list.push(entity);
      this.selection = { kind, id: entity.id };
      this.$emit(`add-${kind}`, entity);
    },
    onMoveEntity({ kind, id, dx, dy }) {
      const entity = this.findEntity(kind, id);
      if (!entity) return;
      if (kind === "image" && entity.locked) return;
      this.pushHistory(`move-${kind}-${id}`);
      // PAREDE (bezier): translada TODOS os pontos, não só um x/y único
      if (entity.shape === "bezier" && Array.isArray(entity.points)) {
        entity.points = entity.points.map(p => ({ x: p.x + dx, y: p.y + dy }));
        entity.x = entity.points[0] ? entity.points[0].x : entity.x;
        entity.y = entity.points[0] ? entity.points[0].y : entity.y;
        this.$emit(`update-${kind}`, { id, patch: { points: entity.points, x: entity.x, y: entity.y } });
        return;
      }
      entity.x += dx;
      entity.y += dy;
      this.$emit(`update-${kind}`, { id, patch: { x: entity.x, y: entity.y } });
    },
    // ---- PAREDE (utility:wall): criação e edição dos pontos/curvatura ----
    onCreateWall(points) {
      if (this.readonly) return;
      const wall = createWallElement(points);
      this.addEntity("element", wall);
    },
    onMoveWallPoint({ id, pointIndex, dx, dy }) {
      const entity = this.findEntity("element", id);
      if (!entity || !Array.isArray(entity.points) || !entity.points[pointIndex]) return;
      this.pushHistory(`wall-point-${id}-${pointIndex}`);
      const points = entity.points.map((p, i) =>
        i === pointIndex ? { x: p.x + dx, y: p.y + dy } : p
      );
      entity.points = points;
      if (pointIndex === 0) {
        entity.x = points[0].x;
        entity.y = points[0].y;
      }
      // ângulo de cada quina é "registrado" no próprio elemento (não só
      // recalculado na hora de desenhar) — sempre que um ponto se move.
      const angles = computeWallAngles(points);
      entity.angles = angles;
      this.$emit("update-element", { id, patch: { points, angles, x: entity.x, y: entity.y } });
    },
    onMoveWallBow({ id, segIndex, dx, dy }) {
      const entity = this.findEntity("element", id);
      if (!entity || !Array.isArray(entity.points)) return;
      const p0 = entity.points[segIndex];
      const p1 = entity.points[segIndex + 1];
      if (!p0 || !p1) return;
      this.pushHistory(`wall-bow-${id}-${segIndex}`);
      // projeta o delta (px) na NORMAL do segmento — o bow é escalar
      const dxs = p1.x - p0.x;
      const dys = p1.y - p0.y;
      const len = Math.hypot(dxs, dys) || 1;
      const nx = -dys / len;
      const ny = dxs / len;
      const delta = dx * nx + dy * ny;
      const bows = Array.isArray(entity.bows) ? [...entity.bows] : [];
      bows[segIndex] = (Number(bows[segIndex]) || 0) + delta;
      entity.bows = bows;
      this.$emit("update-element", { id, patch: { bows } });
    },
    // arraste da ponta do líder de uma anotação (deltas em px do desenho)
    onMoveLeader({ id, dx, dy }) {
      const entity = this.findEntity("annotation", id);
      if (!entity || !entity.leader) return;
      this.pushHistory(`leader-${id}`);
      entity.leader = { x: entity.leader.x + dx, y: entity.leader.y + dy };
      this.$emit("update-annotation", { id, patch: { leader: entity.leader } });
    },
    onResizeEntity({ kind, id, dx, dy }) {
      const entity = this.findEntity(kind, id);
      if (!entity) return;
      this.pushHistory(`resize-${kind}-${id}`);
      entity.width = Math.max(20, entity.width + dx);
      entity.height = Math.max(20, entity.height + dy);
      this.$emit(`update-${kind}`, {
        id,
        patch: { width: entity.width, height: entity.height }
      });
    },
    onUpdateEntity(kind, id, patch) {
      const entity = this.findEntity(kind, id);
      if (!entity) return;
      this.pushHistory(`upd-${kind}-${id}`);
      Object.assign(entity, patch);
      this.$emit(`update-${kind}`, { id, patch });
    },
    /**
     * Remove um ITEM DA FICHA pelo índice, levando junto o que está
     * vinculado a ele (saias/rodapés com linkedTo e acabamentos de borda ou
     * superfície). O pai recalcula os totais no update:items.
     */
    removeFichaItemByIndex(sourceIndex) {
      if (this.readonly) return;
      const item = this.items[sourceIndex];
      if (!item) return;
      const id = item.fichaId;
      const removedEdgeRef = item.edgeRef || null;
      const removedSurfaceRef = item.surfaceRef || null;
      const next = this.items
        .map(it => {
          if (!it || !it.fichaId) {
            return it;
          }
          if (removedSurfaceRef && it.fichaId === removedSurfaceRef.parentId) {
            return { ...it, surfaceFinishItem: null };
          }
          if (!removedEdgeRef || it.fichaId !== removedEdgeRef.parentId) return it;
          const edgeFinishItems = { ...(it.edgeFinishItems || {}) };
          delete edgeFinishItems[removedEdgeRef.edge];
          return {
            ...it,
            edgeFinishes: (it.edgeFinishes || []).filter(
              edge => edge !== removedEdgeRef.edge
            ),
            edgeFinishItems
          };
        })
        .filter((it, i) => {
          if (i === sourceIndex) return false;
          if (id && it.linkedTo && it.linkedTo.parentId === id) return false;
          if (id && it.edgeRef && it.edgeRef.parentId === id) return false;
          if (id && it.surfaceRef && it.surfaceRef.parentId === id) return false;
          if (id && it.cutoutRef && it.cutoutRef.parentId === id) return false;
          return true;
        });
      this.commitItemsBulk(next, `remove-item-${sourceIndex}`);
      this.selection = { kind: null, id: null };
      this.$emit("notify", {
        type: "success",
        message: `${item.descricao || item.type || "Item"} removido da ficha.`
      });
    },
    onRemoveEntity({ kind, id }) {
      if (!kind || (id === null || id === undefined)) return;
      if (kind === "cutout") {
        this.removeCutout(id);
        return;
      }
      // peça da ficha selecionada no canvas
      if (kind === "piece") {
        const piece = this.pieceById(id);
        if (piece) this.removeFichaItemByIndex(piece.sourceIndex);
        return;
      }
      // item "Acabamento" removido pela legenda (id = sourceIndex)
      if (kind === "finishing") {
        this.removeFichaItemByIndex(id);
        return;
      }
      const list = this.listFor(kind);
      const idx = list.findIndex(e => e.id === id);
      if (idx < 0) return;
      this.pushHistory(`remove-${kind}-${id}`);
      list.splice(idx, 1);
      if (this.selection.id === id) this.selection = { kind: null, id: null };
      this.$emit(`remove-${kind}`, { id });
    },
    addAnnotationAtCenter() {
      this.addEntity(
        "annotation",
        createAnnotation({ x: this.canvasWidth / 2, y: this.canvasHeight / 2 })
      );
    },

    // upload de imagem -> base64 (o pai pode trocar por upload real)
    onFileSelected(evt) {
      const file = evt.target.files && evt.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => {
        const point = this.pendingImagePoint || {
          x: this.canvasWidth / 2,
          y: this.canvasHeight / 2
        };
        this.addEntity(
          "image",
          createDrawingImage({ src: e.target.result, x: point.x, y: point.y, label: file.name })
        );
        this.pendingImagePoint = null;
      };
      reader.readAsDataURL(file);
      evt.target.value = "";
    },

    // ============ helpers internos ============
    listFor(kind) {
      if (kind === "element") return this.elements;
      if (kind === "annotation") return this.annotations;
      if (kind === "image") return this.images;
      return [];
    },
    findEntity(kind, id) {
      return this.listFor(kind).find(e => e.id === id) || null;
    },

    // ============ VIEWPORT ============
    clampZoom(z) {
      return Math.max(0.3, Math.min(4, z));
    },
    zoomBy(factor) {
      // zoom pelos botões: mantém o centro do canvas fixo
      this.zoomAt(factor, this.canvasWidth / 2, this.canvasHeight / 2);
    },
    // zoom mantendo o ponto (x,y) — em coords do viewBox — parado na tela
    zoomAt(factor, x, y) {
      const next = this.clampZoom(this.zoom * factor);
      const ratio = next / this.zoom;
      this.pan = {
        x: x - (x - this.pan.x) * ratio,
        y: y - (y - this.pan.y) * ratio
      };
      this.zoom = next;
    },
    onWheelZoom({ factor, x, y }) {
      this.zoomAt(factor, x, y);
    },
    onPanBy({ dx, dy }) {
      this.pan = { x: this.pan.x + dx, y: this.pan.y + dy };
    },
    // fit-to-content: enquadra todas as peças (com folga para as cotas)
    centerView() {
      const pieces = getRenderablePieces(this.project.pieces);
      const bounds = getContentBounds(pieces, {
        scale: this.project.scale,
        width: this.canvasWidth,
        height: this.canvasHeight
      });
      if (!bounds || bounds.w <= 0 || bounds.h <= 0) {
        this.resetView();
        return;
      }
      const zoom = this.clampZoom(
        Math.min(this.canvasWidth / bounds.w, this.canvasHeight / bounds.h)
      );
      this.zoom = zoom;
      this.pan = {
        x: (this.canvasWidth - bounds.w * zoom) / 2 - bounds.x * zoom,
        y: (this.canvasHeight - bounds.h * zoom) / 2 - bounds.y * zoom
      };
    },
    resetView() {
      this.zoom = 1;
      this.pan = { x: 0, y: 0 };
    }
  }
};
</script>

<style scoped>
.std-root {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.std-root--dark {
  background: #0b141a;
  color: #e9edef;
}
.std-root > .std-toolbar {
  left: 50%;
  max-width: calc(100% - 28px);
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  z-index: 6;
}
.std-top-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 9px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
  color: #173b33;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  left: 50%;
  padding: 0;
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  width: 36px;
  z-index: 7;
}
.std-root--compact > .std-toolbar {
  top: 46px;
}
.std-root--dark .std-top-toggle {
  background: #202c33;
  border-color: #26343c;
  color: #e9edef;
}
.std-root--dark ::v-deep .std-toolbar-group {
  background: #182229;
  border-color: #26343c;
}
.std-root--dark ::v-deep .std-tool-btn {
  color: #e9edef !important;
}
.std-root--dark ::v-deep .std-tool-btn:not(.white--text) {
  background: #202c33 !important;
}
.std-body {
  display: flex;
  flex: 1;
  height: 100%;
  min-height: 0;
  position: relative;
}
.std-stage {
  flex: 1 1 auto;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative; /* ancora o editor inline de cota */
}
.std-board-head {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(150px, 1fr) auto minmax(150px, 1fr);
  left: 10px;
  position: absolute;
  right: 10px;
  top: 8px;
  z-index: 7;
}
.std-board-title {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(7, 94, 84, 0.12);
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  color: #173b33;
  display: inline-flex;
  gap: 8px;
  min-height: 36px;
  min-width: 0;
  padding: 6px 10px;
  width: fit-content;
}
.std-board-title strong {
  display: block;
  font-size: 13px;
  line-height: 1.15;
}
.std-board-title span {
  color: #74827c;
  display: block;
  font-size: 10px;
  line-height: 1.15;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.std-board-toolbar {
  justify-self: center;
  max-width: 100%;
}
.std-board-actions {
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
}
.std-root--dark .std-board-title {
  background: rgba(24, 34, 41, 0.96);
  border-color: #26343c;
  color: #e9edef;
}
.std-root--dark .std-board-title span {
  color: #aebac1;
}
.std-stage ::v-deep .std-canvas,
.std-stage ::v-deep .std-iso {
  height: 100%;
  width: 100%;
}
.std-iso-frame {
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 14px;
  background: #ffffff;
  height: 100%;
  width: 100%;
}
.std-root--dark .std-iso-frame {
  background: #0b141a;
  border-color: #26343c;
}
.std-finish-palette {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(7, 94, 84, 0.12);
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  left: 50%;
  max-width: calc(100% - 32px);
  overflow-x: auto;
  padding: 4px 7px;
  position: absolute;
  top: 48px;
  transform: translateX(-50%);
  z-index: 5;
}
.std-root--dark .std-finish-palette,
.std-root--dark .std-finish-list-float,
.std-root--dark .std-piece-list-float,
.std-root--dark .std-stage-foot,
.std-root--dark .std-secondary-menu,
.std-root--dark .std-piece-menu,
.std-root--dark .std-dim-editor {
  background: rgba(24, 34, 41, 0.96);
  border-color: #26343c;
  color: #e9edef;
}
.std-finish-palette-label {
  align-items: center;
  color: #74827c;
  display: inline-flex;
  font-size: 11px;
  gap: 4px;
}
.std-root--dark .std-finish-palette-label,
.std-root--dark .std-piece-list-title,
.std-root--dark .std-secondary-menu-title,
.std-root--dark .std-piece-menu-title,
.std-root--dark .std-piece-list-size,
.std-root--dark .std-dim-unit {
  color: #aebac1;
}
.std-finish-chip {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 8px;
  color: #173b33;
  cursor: grab;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 6px;
  padding: 3px 8px 3px 4px;
  user-select: none;
}
.std-root--dark .std-finish-chip,
.std-root--dark .std-finish-list-toggle,
.std-root--dark .std-piece-list-toggle,
.std-root--dark .std-ruler-dot,
.std-root--dark .std-ruler-preview,
.std-root--dark .std-ruler-eye {
  background: #202c33;
  border-color: #26343c;
  color: #e9edef;
}
.std-root--dark .std-finish-chip:hover,
.std-root--dark .std-piece-list-item:hover,
.std-root--dark .std-piece-list-item.active,
.std-root--dark .std-secondary-menu-item:hover,
.std-root--dark .std-piece-menu-item:hover,
.std-root--dark .std-piece-menu-item.active,
.std-root--dark .std-ruler-dot:hover,
.std-root--dark .std-ruler-dot.active {
  background: #0f3d36;
  border-color: #25d366;
  color: #e9edef;
}
.std-finish-chip:active {
  cursor: grabbing;
}
.std-finish-chip:hover {
  border-color: #128c7e;
  box-shadow: 0 2px 8px rgba(18, 140, 126, 0.18);
}
.std-finish-chip-img {
  border-radius: 5px;
  height: 22px;
  object-fit: cover;
  width: 22px;
}
.std-piece-list-float {
  align-items: stretch;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(7, 94, 84, 0.14);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  left: 10px;
  max-height: calc(100% - 92px);
  overflow: visible;
  padding: 8px;
  position: absolute;
  top: 58px;
  width: 218px;
  z-index: 7;
}
.std-root--compact .std-piece-list-float {
  top: 52px;
}
.std-piece-list-float.collapsed {
  align-items: center;
  height: 36px;
  justify-content: center;
  padding: 4px;
  width: 36px;
}
.std-piece-list-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: inline-flex;
  height: 26px;
  justify-content: center;
  margin: 0 0 6px auto;
  padding: 0;
  position: relative;
  right: auto;
  top: auto;
  width: 26px;
  z-index: 2;
}
.std-piece-list-float.collapsed .std-piece-list-toggle {
  margin: 0;
  right: auto;
  top: auto;
}
.std-piece-list-content {
  max-height: calc(100dvh - 132px);
  overflow: auto;
  padding-right: 0;
}
.std-finish-list-float {
  align-items: stretch;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(7, 94, 84, 0.14);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 92px);
  overflow: visible;
  padding: 8px;
  position: absolute;
  right: 10px;
  top: 58px;
  width: 218px;
  z-index: 7;
}
.std-finish-list-float.side-open {
  right: 288px;
}
.std-root--compact .std-finish-list-float {
  top: 52px;
}
.std-finish-list-float.collapsed {
  align-items: center;
  height: 36px;
  justify-content: center;
  padding: 4px;
  width: 36px;
}
.std-finish-list-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: inline-flex;
  height: 26px;
  justify-content: center;
  margin: 0 auto 6px 0;
  padding: 0;
  position: relative;
  width: 26px;
  z-index: 2;
}
.std-finish-list-float.collapsed .std-finish-list-toggle {
  margin: 0;
}
.std-finish-list-content {
  display: grid;
  gap: 6px;
  max-height: calc(100dvh - 132px);
  overflow: auto;
}
.std-finish-list-title {
  align-items: center;
  color: #075e54;
  display: flex;
  font-size: 11px;
  font-weight: 800;
  gap: 5px;
}
.std-finish-list-title span {
  background: #e2f4ee;
  border-radius: 999px;
  color: #075e54;
  font-size: 9px;
  margin-left: auto;
  padding: 1px 6px;
}
.std-piece-list-title {
  color: #74827c;
  display: none;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.35px;
  margin: 0 18px 6px 2px;
  text-transform: uppercase;
}
.std-piece-list-item[style*="display: none"] {
  margin: 0;
  padding: 0;
}
.std-piece-tabs {
  background: #f3f8f5;
  border: 1px solid rgba(7, 94, 84, 0.1);
  border-radius: 8px;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 0 8px;
  padding: 2px;
}
.std-piece-tab {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #5a6660;
  cursor: pointer;
  display: flex;
  font-size: 10px;
  font-weight: 800;
  justify-content: center;
  min-width: 0;
  padding: 5px 3px;
  white-space: nowrap;
}
.std-piece-tab span {
  color: #8a9a93;
  font-size: 9px;
  margin-left: 2px;
}
.std-piece-tab.active {
  background: #ffffff;
  color: #075e54;
  box-shadow: 0 1px 4px rgba(7, 94, 84, 0.12);
}
.std-tab-panel {
  display: grid;
  gap: 4px;
}
.std-piece-list-item {
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: grid;
  gap: 1px;
  grid-template-columns: 34px 1fr;
  padding: 6px;
  text-align: left;
  width: 100%;
}
.std-piece-list-item--action {
  grid-template-columns: 28px 1fr 24px;
}
.std-root--dark .std-piece-list-item,
.std-root--dark .std-secondary-menu-item,
.std-root--dark .std-piece-menu-item {
  color: #e9edef;
}
.std-piece-list-item:hover,
.std-piece-list-item.active {
  background: #e2f4ee;
}
.std-piece-list-code {
  font-size: 11px;
  font-weight: 800;
  grid-row: span 2;
}
.std-piece-list-name {
  font-size: 12px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.std-piece-list-size {
  color: #74827c;
  font-size: 10px;
}
.std-piece-list-empty {
  color: #8a9a93;
  font-size: 11px;
  padding: 6px 4px;
}
.std-piece-list-remove {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #74827c;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 24px;
}
.std-piece-list-remove:hover {
  background: #fff1f0;
  color: #b42318;
}
.std-root--dark .std-piece-tabs {
  background: #111b21;
  border-color: #26343c;
}
.std-root--dark .std-piece-tab {
  color: #aebac1;
}
.std-root--dark .std-piece-tab.active {
  background: #202c33;
  color: #25d366;
}
.std-root--dark .std-piece-tab span,
.std-root--dark .std-piece-list-empty {
  color: #74838c;
}
.std-root--dark .std-finish-list-title {
  color: #25d366;
}
.std-root--dark .std-finish-list-title span {
  background: #0f3d36;
  color: #25d366;
}
.std-root--dark .std-piece-list-remove:hover {
  background: #3a1f24;
}
.std-edge-menu-overlay {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 7;
}
.std-secondary-menu {
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.18);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  max-height: 280px;
  min-width: 190px;
  overflow-y: auto;
  padding: 6px;
  position: absolute;
  z-index: 8;
}
.std-secondary-menu-title {
  color: #74827c;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin: 2px 6px 6px;
  text-transform: uppercase;
}
.std-secondary-menu-item {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 8px;
  padding: 5px 8px;
  text-align: left;
  width: 100%;
}
.std-secondary-menu-item:hover {
  background: #f3f8f5;
}
.std-secondary-menu-thumb {
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 4px;
  flex-shrink: 0;
  height: 24px;
  object-fit: cover;
  width: 24px;
}
.std-piece-menu {
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.18);
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
  min-width: 168px;
  padding: 8px;
  position: absolute;
  z-index: 8;
}
.std-piece-menu-title {
  color: #74827c;
  font-size: 10px;
  font-weight: 700;
  margin: 0 2px 7px;
  text-transform: uppercase;
}
.std-ruler-picker {
  display: grid;
  grid-template-columns: 28px 48px 28px;
  grid-template-rows: 24px 34px 24px;
  justify-content: center;
  margin-bottom: 7px;
}
.std-ruler-preview {
  align-items: center;
  align-self: center;
  background: #f3f8f5;
  border: 1.5px solid rgba(7, 94, 84, 0.28);
  border-radius: 6px;
  display: flex;
  grid-column: 2;
  grid-row: 2;
  height: 28px;
  justify-content: center;
  width: 48px;
}
.std-ruler-preview span {
  background: #128c7e;
  border-radius: 999px;
  display: block;
  height: 3px;
  width: 26px;
}
.std-root--dark .std-ruler-preview span,
.std-root--dark .std-ruler-mini-dim {
  background: #25d366;
}
.std-root--dark .std-ruler-mini-dim--h::before {
  border-color: transparent #25d366 transparent transparent;
}
.std-root--dark .std-ruler-mini-dim--h::after {
  border-color: transparent transparent transparent #25d366;
}
.std-root--dark .std-ruler-mini-dim--v::before {
  border-color: transparent transparent #25d366 transparent;
}
.std-root--dark .std-ruler-mini-dim--v::after {
  border-color: #25d366 transparent transparent transparent;
}
.std-ruler-dot {
  align-items: center;
  background: #f7faf8;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 8px;
  color: #5a6660;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  justify-self: center;
  padding: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  width: 24px;
  position: relative;
}
.std-ruler-dot:hover,
.std-ruler-dot.active {
  background: #e2f4ee;
  border-color: #128c7e;
  color: #075e54;
}
.std-ruler-mini-dim {
  background: #173b33;
  display: block;
  position: relative;
}
.std-ruler-mini-dim::before,
.std-ruler-mini-dim::after {
  border-style: solid;
  content: "";
  height: 0;
  position: absolute;
  width: 0;
}
.std-ruler-mini-dim--h {
  height: 1.5px;
  width: 14px;
}
.std-ruler-mini-dim--h::before {
  border-color: transparent #173b33 transparent transparent;
  border-width: 3px 5px 3px 0;
  left: -4px;
  top: -2px;
}
.std-ruler-mini-dim--h::after {
  border-color: transparent transparent transparent #173b33;
  border-width: 3px 0 3px 5px;
  right: -4px;
  top: -2px;
}
.std-ruler-mini-dim--v {
  height: 14px;
  width: 1.5px;
}
.std-ruler-mini-dim--v::before {
  border-color: transparent transparent #173b33 transparent;
  border-width: 0 3px 5px 3px;
  left: -2px;
  top: -4px;
}
.std-ruler-mini-dim--v::after {
  border-color: #173b33 transparent transparent transparent;
  border-width: 5px 3px 0 3px;
  bottom: -4px;
  left: -2px;
}
.std-ruler-eye {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.14);
  border-radius: 999px;
  bottom: -6px;
  color: #9aa6a0;
  display: inline-flex;
  height: 16px;
  justify-content: center;
  position: absolute !important;
  right: -8px;
  width: 16px;
}
.std-ruler-eye.active {
  color: #075e54;
}
.std-ruler-dot--top {
  grid-column: 2;
  grid-row: 1;
}
.std-ruler-dot--left {
  grid-column: 1;
  grid-row: 2;
  align-self: center;
}
.std-ruler-dot--right {
  grid-column: 3;
  grid-row: 2;
  align-self: center;
}
.std-ruler-dot--bottom {
  grid-column: 2;
  grid-row: 3;
}
.std-piece-menu-item {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #173b33;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: 8px;
  padding: 7px 8px;
  text-align: left;
  width: 100%;
}
.std-piece-menu-item:hover {
  background: #f3f8f5;
}
.std-piece-menu-item.active {
  background: #e2f4ee;
  color: #075e54;
}
.std-piece-menu-split {
  display: grid;
  gap: 4px;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 7px;
}
.std-piece-menu-split .std-piece-menu-item {
  font-size: 11px;
  justify-content: center;
  padding: 6px 5px;
}
.std-piece-menu-stack {
  display: grid;
  gap: 4px;
  margin-bottom: 7px;
}
.std-piece-menu-stack .std-piece-menu-item {
  justify-content: flex-start;
  padding: 7px 8px;
}
.std-piece-menu-item--danger {
  color: #b42318;
}
.std-piece-menu-item--danger:hover {
  background: #fff1f0;
}
.std-dim-editor {
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #173b33;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  display: flex;
  gap: 2px;
  padding: 2px 6px;
  position: absolute;
  z-index: 5;
}
.std-dim-input {
  border: none;
  color: #173b33;
  font-size: 12px;
  font-weight: 700;
  outline: none;
  text-align: right;
  width: 52px;
}
.std-root--dark .std-dim-input {
  background: transparent;
  color: #e9edef;
}
.std-dim-unit {
  color: #74827c;
  font-size: 11px;
  font-weight: 600;
}
.std-stage-foot {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  bottom: 10px;
  left: 10px;
  max-height: 120px;
  max-width: min(420px, calc(100% - 20px));
  overflow: auto;
  position: absolute;
  z-index: 3;
}
.std-side {
  bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(300px, calc(100% - 32px));
  overflow: auto;
  position: absolute;
  right: 10px;
  top: 58px;
  width: 270px;
  z-index: 5;
}
.std-side-card {
  border-radius: 12px !important;
  padding: 8px;
}
.std-side-title {
  font-size: 11px;
  font-weight: 700;
  color: #173b33;
  margin-bottom: 4px;
}
/* painel lateral no tema escuro */
.std-side--dark .std-side-card {
  background: #182229 !important;
  border-color: #26343c !important;
}
.std-side--dark .std-side-title {
  color: #e9edef;
}
.std-side--dark ::v-deep .std-inspector-title {
  color: #e9edef;
}
.std-side--dark ::v-deep .std-inspector-sub,
.std-side--dark ::v-deep .std-field-label {
  color: #aebac1;
}
.std-side--dark ::v-deep .std-finish-hint,
.std-side--dark ::v-deep .std-finish-note {
  background: #202c33;
  border-color: #26343c;
  color: #aebac1;
}
.std-side--dark ::v-deep .std-applied-finish {
  color: #e9edef;
}
.std-hidden-file {
  display: none;
}

/* modo compacto: empilha o painel lateral abaixo do canvas em telas pequenas */
@media (max-width: 700px) {
  .std-board-head {
    grid-template-columns: 1fr;
  }
  .std-board-title,
  .std-board-actions {
    display: none;
  }
  .std-board-toolbar {
    max-width: 100%;
  }
  .std-body {
    flex-direction: column;
  }
  .std-finish-list-float.side-open {
    right: 10px;
  }
  .std-side {
    flex-basis: auto;
  }
}
</style>
